import React from "react";
import ModuleHeaderMenu from "../components/ModuleHeaderMenu";
import { buildNextRecordNumber, mapCostCenterCodes } from "../lib/recordNumberUtils";
import {
  appendProvisionalSuffix,
  appendUpdatePendingDisplaySuffix,
  markOperationalCreatePending,
  markOperationalUpdatePending,
} from "../lib/operationalProvisional";

export function createFinancialModule(deps){
  const {
    useState,
    useEffect,
    makeSeed,
    useSysLists,
    useFarm,
    useFarmCollection,
    useGlobalCollection,
    useToast,
    useBgSyncToast,
    useReliableSyncOnSave,
    canCreateInModule,
    canEditInModule,
    canDeleteInModule,
    canViewCostCenter,
    getAllowedCostCentersForModule,
    getDeniedCostCentersForModule,
    T,
    FIN_CATS,
    FIN_TYPES,
    COST_CENTERS,
    today,
    fmtDate,
    fmtMoney,
    isCompactViewport,
    createClientId,
    makeCreateMeta,
    makeUpdateMeta,
    Btn,
    Field,
    Input,
    Select,
    Textarea,
    StatCard,
    Toast,
    Badge,
    CCBadge,
  } = deps;

  const STATUS_OPTIONS = ["Programado", "Lançado", "Pago", "Recebido", "Cancelado"];
  const PAYMENT_STATUSES = new Set(["Pago", "Recebido"]);

  const monthStart = (monthKey) => `${monthKey}-01`;
  const monthEnd = (monthKey) => {
    const [y, m] = String(monthKey || "").split("-").map(Number);
    if(!y || !m) return `${today().slice(0,7)}-31`;
    return new Date(y, m, 0).toISOString().slice(0,10);
  };
  const monthLabel = (monthKey) => {
    const [y, m] = String(monthKey || "").split("-").map(Number);
    if(!y || !m) return monthKey || "";
    return new Date(y, m - 1, 1).toLocaleDateString("pt-BR", { month:"long", year:"numeric" });
  };
  const monthFromDate = (dateLike) => String(dateLike || today()).slice(0,7);
  const toNum = (v) => Number(v || 0);

  const isDeletedFinancialEntry = (entry) => Boolean(entry?._deleted || entry?.deleted || entry?.deletedAt || entry?.deleted_at);
  const financialLastActivityTime = (entry) => {
    const value = entry?.updatedAt || entry?.updated_at || entry?.modifiedAt || entry?.createdAt || entry?.created_at || entry?.date || entry?.dueDate || entry?.id || "";
    const parsed = Date.parse(String(value || ""));
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const compareFinancialByLastActivity = (a, b) => financialLastActivityTime(b) - financialLastActivityTime(a) || String(b?.id || "").localeCompare(String(a?.id || ""));
  const displayFinancialRecordNumber = (record) => {
    if (!record) return "—";
    const rn = String(record.recordNumber || record.id || "").trim();
    if (!rn) return "—";
    if (record.pendingLocalUpdate && !record.pendingLocalCreate) return appendUpdatePendingDisplaySuffix(rn);
    return rn;
  };

  return function FinancialModule({session, isGestor, moduleCardsConfig: moduleCardsConfigProp}){
    const seed = makeSeed();
    const SL = useSysLists();
    const [moduleCardsConfigLocal, setModuleCardsConfigLocal] = useState(()=>{
      try{
        const parsed = JSON.parse(localStorage.getItem("fz:moduleCardsConfig") || "null");
        return parsed && typeof parsed === "object" ? parsed : {};
      }catch(_){ return {}; }
    });
    const [adminGlobalsConfigRows] = useGlobalCollection("adminGlobalsConfig", [{ id:"admin_globals_config", clientId:"admin_globals_config", moduleCardsConfig:{} }]);
    const { fid: financialFarmId } = useFarm();
    const { fid: currentFarmId } = useFarm();
    const [entries, setEntries] = useFarmCollection("financial", seed.financial);
    const [closingLocks, setClosingLocks] = useFarmCollection("financialClosingLocks", []);
    const [employees] = useFarmCollection("employees", seed.employees || []);
    const [stockItems] = useGlobalCollection("stock", []);
    const [stockMovements] = useFarmCollection("stockMovements", []);
    const [form, setForm] = useState(null);
    const costCenterCodeMap = React.useMemo(()=>mapCostCenterCodes(SL?.COST_CENTER_CODES || []), [SL]);
    const financialInlineTopRef = React.useRef(null);
    const financialInlineRefs = React.useRef(new Map());
    const [tab, setTab] = useState("dashboard");
    const [ft, setFt] = useState("Todos");
    const [fcc, setFcc] = useState("Todos");
    const [competenceMonth, setCompetenceMonth] = useState(today().slice(0,7));
    const [toast, show] = useToast();
    useBgSyncToast("financial", show);
    const syncOnSave = useReliableSyncOnSave();
    const canCreate = canCreateInModule(session, "financial") || canEditInModule(session, "financial");
    const canEdit = canEditInModule(session, "financial");
    const canDelete = canDeleteInModule(session, "financial");

    React.useEffect(()=>{
      if(!form) return;
      const target = financialInlineTopRef.current || financialInlineRefs.current.get(String(form?.id||""));
      if(!target) return;
      const scrollToInlineTop = ()=>{
        const rect = target.getBoundingClientRect?.();
        const top = rect ? Math.max(0, window.scrollY + rect.top - 12) : null;
        if(top != null && typeof window.scrollTo === "function") {
          window.scrollTo({ top, behavior:"smooth" });
          return;
        }
        if(typeof target.scrollIntoView === "function") target.scrollIntoView({behavior:"smooth", block:"start"});
      };
      const timer = setTimeout(scrollToInlineTop, 40);
      return ()=>clearTimeout(timer);
    }, [form]);
    const allowedCostCenters = React.useMemo(()=> getAllowedCostCentersForModule(session, "financial"), [session]);
    const deniedCostCenters = React.useMemo(()=> getDeniedCostCentersForModule(session, "financial"), [session]);

    useEffect(()=>{
      function reloadModuleCardsConfig(){
        try{
          const parsed = JSON.parse(localStorage.getItem("fz:moduleCardsConfig") || "null");
          setModuleCardsConfigLocal(parsed && typeof parsed === "object" ? parsed : {});
        }catch(_){ setModuleCardsConfigLocal({}); }
      }
      reloadModuleCardsConfig();
      window.addEventListener("fz:modulecards:updated", reloadModuleCardsConfig);
      window.addEventListener("storage", reloadModuleCardsConfig);
      window.addEventListener("focus", reloadModuleCardsConfig);
      return ()=>{
        window.removeEventListener("fz:modulecards:updated", reloadModuleCardsConfig);
        window.removeEventListener("storage", reloadModuleCardsConfig);
        window.removeEventListener("focus", reloadModuleCardsConfig);
      };
    }, []);

    const syncedModuleCardsConfig = React.useMemo(()=>{
      const row = Array.isArray(adminGlobalsConfigRows) ? adminGlobalsConfigRows[0] : null;
      return row?.moduleCardsConfig && typeof row.moduleCardsConfig === "object" ? row.moduleCardsConfig : {};
    }, [adminGlobalsConfigRows]);

    const moduleCardsConfig = React.useMemo(()=>{
      const globalConfig = syncedModuleCardsConfig && typeof syncedModuleCardsConfig === "object" ? syncedModuleCardsConfig : {};
      const propConfig = moduleCardsConfigProp && typeof moduleCardsConfigProp === "object" ? moduleCardsConfigProp : {};
      const localConfig = moduleCardsConfigLocal && typeof moduleCardsConfigLocal === "object" ? moduleCardsConfigLocal : {};
      return {
        ...globalConfig,
        ...propConfig,
        ...localConfig,
        financial: {
          ...(globalConfig?.financial && typeof globalConfig.financial === "object" ? globalConfig.financial : {}),
          ...(propConfig?.financial && typeof propConfig.financial === "object" ? propConfig.financial : {}),
          ...(localConfig?.financial && typeof localConfig.financial === "object" ? localConfig.financial : {}),
        },
      };
    }, [syncedModuleCardsConfig, moduleCardsConfigProp, moduleCardsConfigLocal]);

    const getConfiguredSectionCards = React.useCallback((sectionId, fallback=[])=>{
      const moduleConfig = moduleCardsConfig?.financial;
      const aliases = {
        "moduleHome": ["moduleHome"],
        "menu:dashboard": ["menu:dashboard", "menu:financeiro"],
        "menu:entries": ["menu:entries", "menu:lancamentos"],
        "menu:payroll": ["menu:payroll", "menu:folha"],
        "menu:stock": ["menu:stock", "menu:estoque"],
        "menu:reconciliation": ["menu:reconciliation", "menu:conciliacao"],
        "menu:reports": ["menu:reports", "menu:relatorios"],
        "menu:dre": ["menu:dre"],
        "menu:pending": ["menu:pending", "menu:rastreio"],
        "menu:closing": ["menu:closing", "menu:fechamento"],
      };
      const keys = aliases[sectionId] || [sectionId];
      for(const key of keys){
        if(moduleConfig && Object.prototype.hasOwnProperty.call(moduleConfig, key)){
          const configured = moduleConfig?.[key];
          if(Array.isArray(configured)) return configured.map(v=>String(v||"")).filter(Boolean);
          return [];
        }
      }
      return (Array.isArray(fallback) ? fallback : []).map(v=>String(v||"")).filter(Boolean);
    }, [moduleCardsConfig]);

    const chartAccounts = React.useMemo(() => (
      Array.isArray(SL.FIN_CHART_ACCOUNTS) && SL.FIN_CHART_ACCOUNTS.length ? SL.FIN_CHART_ACCOUNTS : []
    ), [SL.FIN_CHART_ACCOUNTS]);
    const payrollCharges = React.useMemo(() => (
      Array.isArray(SL.PAYROLL_CHARGES) && SL.PAYROLL_CHARGES.length ? SL.PAYROLL_CHARGES : []
    ), [SL.PAYROLL_CHARGES]);
    const taxRegimes = React.useMemo(() => (
      Array.isArray(SL.TAX_REGIMES) && SL.TAX_REGIMES.length ? SL.TAX_REGIMES : []
    ), [SL.TAX_REGIMES]);

    const normalizeClosingLocks = React.useCallback((items) => {
      const map = new Map();
      for(const raw of (Array.isArray(items) ? items : [])){
        const monthKey = String(raw?.monthKey || raw?.competenceMonth || "").slice(0,7);
        if(!monthKey) continue;
        const prev = map.get(monthKey);
        const prevTs = String(prev?.updatedAt || prev?.createdAt || "");
        const nextTs = String(raw?.updatedAt || raw?.createdAt || "");
        const normalized = {
          id: raw?.id || createClientId("finlock"),
          monthKey,
          mode: ["open","soft","hard"].includes(String(raw?.mode || "")) ? String(raw.mode) : "open",
          notes: String(raw?.notes || ""),
          lockedBy: String(raw?.lockedBy || raw?.updatedBy || ""),
          createdAt: raw?.createdAt || raw?.updatedAt || new Date().toISOString(),
          updatedAt: raw?.updatedAt || raw?.createdAt || new Date().toISOString(),
        };
        if(!prev || nextTs >= prevTs) map.set(monthKey, normalized);
      }
      return Array.from(map.values()).sort((a,b)=>String(a.monthKey).localeCompare(String(b.monthKey)));
    }, []);

    const getTaxesForModel = React.useCallback((taxModel) => {
      const regime = taxRegimes.find(item=>String(item?.name||"")===String(taxModel||""));
      return Array.isArray(regime?.taxes) ? regime.taxes.map(v=>String(v||"").trim()).filter(Boolean) : [];
    }, [taxRegimes]);

    const buildTaxBreakdown = React.useCallback((entryLike) => {
      const amount = Math.max(0, Number(entryLike?.amount || 0));
      const availableTaxes = getTaxesForModel(entryLike?.taxModel);
      const source = Array.isArray(entryLike?.taxBreakdown) ? entryLike.taxBreakdown : [];
      const normalizedSource = source.map(item=>({
        name: String(item?.name || item?.tax || "").trim(),
        pct: Number(item?.pct || item?.percent || 0),
        amount: Number(item?.amount || item?.value || 0),
        included: item?.included !== false,
      })).filter(item=>item.name);
      const seen = new Set();
      const merged = [];
      for(const item of normalizedSource){
        seen.add(item.name);
        merged.push({
          name: item.name,
          pct: item.pct,
          amount: item.amount,
          included: item.included,
        });
      }
      for(const taxName of availableTaxes){
        if(seen.has(taxName)) continue;
        merged.push({ name: taxName, pct: 0, amount: 0, included: true });
      }
      return merged.map(item=>({
        ...item,
        amount: item.pct > 0 ? Number(((amount * item.pct) / 100).toFixed(2)) : Number((item.amount || 0).toFixed(2)),
      }));
    }, [getTaxesForModel]);

    const dedupeBy = React.useCallback((list, keyFn) => {
      const map = new Map();
      for(const item of (Array.isArray(list) ? list : [])){
        const key = keyFn(item);
        const prev = map.get(key);
        const prevTs = String(prev?.updatedAt || prev?.createdAt || "");
        const nextTs = String(item?.updatedAt || item?.createdAt || "");
        if(!prev || nextTs >= prevTs) map.set(key || createClientId('fin'), item);
      }
      return Array.from(map.values());
    }, []);

    const categoryToAccount = React.useCallback((category, type)=>{
      const cat = String(category||"").toLowerCase();
      const match = chartAccounts.find(acc=>{
        const name = String(acc?.name||"").toLowerCase();
        if(type && String(acc?.type||"").toLowerCase() !== String(type).toLowerCase()) return false;
        return name.includes(cat) || cat.includes(name);
      });
      return match || chartAccounts.find(acc=>String(acc?.type||"")===type) || null;
    }, [chartAccounts]);

    const getAccount = React.useCallback((entryLike) => {
      const direct = chartAccounts.find(acc=>String(acc?.code||"")===String(entryLike?.accountCode||""));
      return direct || categoryToAccount(entryLike?.category, entryLike?.type) || null;
    }, [chartAccounts, categoryToAccount]);

    const validateEntry = React.useCallback((entryLike) => {
      const draft = entryLike || {};
      const account = getAccount(draft);
      const status = String(draft?.status || "Lançado");
      const taxes = Array.isArray(draft?.taxBreakdown) ? draft.taxBreakdown.filter(t=>t?.included !== false && (Number(t?.amount||0) > 0 || Number(t?.pct||0) > 0 || String(t?.name||"").trim())) : [];
      const justification = String(draft?.justificationMissing || "").trim();
      const issues = [];
      const warnings = [];
      if(!String(draft?.description || "").trim()) issues.push("Descrição obrigatória.");
      if(Number(draft?.amount || 0) <= 0) issues.push("Valor maior que zero é obrigatório.");
      if(!String(draft?.accountCode || account?.code || "").trim()) warnings.push("Sem conta contábil definida.");
      const operationalStatus = !["Programado", "Cancelado"].includes(status);
      if(operationalStatus && !String(draft?.documentNumber || "").trim() && !justification) issues.push("Documento/NF obrigatório ou justificativa obrigatória.");
      if(PAYMENT_STATUSES.has(status) && !String(draft?.paymentDate || "").trim()) issues.push("Data de pagamento/recebimento obrigatória quando o status for pago ou recebido.");
      if(account?.taxable && !taxes.length && !justification) issues.push("Detalhamento tributário obrigatório para conta tributável ou informe justificativa.");
      if(String(draft?.originModule || "") !== "manual" && !String(draft?.originId || "").trim()) warnings.push("Lançamento automático sem origem rastreável.");
      if(!String(draft?.costCenter || "").trim()) warnings.push("Centro de custo não informado.");
      if(String(draft?.dueDate || "") && String(draft?.date || "") && String(draft.dueDate) < String(draft.date)) warnings.push("Vencimento anterior à data do lançamento.");
      return { issues, warnings, account, taxes, isValid: issues.length === 0 };
    }, [getAccount]);

    const normalizeEntry = React.useCallback((item) => {
      if(!item || typeof item !== "object") return item;
      const now = new Date().toISOString();
      const resolvedType = item.type || "Despesa";
      const resolvedCategory = item.category || FIN_CATS[0];
      const account = item.accountCode ? chartAccounts.find(acc=>String(acc.code)===String(item.accountCode)) : categoryToAccount(resolvedCategory, resolvedType);
      const resolvedTaxModel = item.taxModel || taxRegimes?.[0]?.name || "Atual";
      const stableId = typeof item.id === "string" && item.id ? item.id : (typeof item.clientId === "string" && item.clientId ? item.clientId : createClientId("fin"));
      return {
        ...item,
        id: stableId,
        clientId: item.clientId || stableId,
        farmId: item.farmId || currentFarmId || financialFarmId || "",
        originModule: item.originModule || item.sourceModule || "manual",
        originId: item.originId || item.sourceId || "",
        automatic: !!item.automatic,
        status: item.status || "Lançado",
        competenceMonth: item.competenceMonth || monthFromDate(item.date || item.dueDate || today()),
        type: resolvedType,
        category: resolvedCategory,
        accountCode: item.accountCode || account?.code || "",
        accountName: item.accountName || account?.name || "",
        description: String(item.description || "").trim(),
        amount: Number(item.amount || 0),
        date: item.date || today(),
        dueDate: item.dueDate || item.date || today(),
        paymentDate: item.paymentDate || "",
        costCenter: item.costCenter || account?.costCenter || COST_CENTERS[0],
        counterCostCenter: item.counterCostCenter || "",
        paymentMethod: item.paymentMethod || "",
        documentNumber: item.documentNumber || item.nfNum || "",
        taxModel: resolvedTaxModel,
        taxBreakdown: buildTaxBreakdown({ ...item, taxModel: resolvedTaxModel, amount: Number(item.amount || 0) }),
        notes: item.notes || "",
        justificationMissing: item.justificationMissing || "",
        metadata: item.metadata && typeof item.metadata === 'object' ? item.metadata : {},
        createdAt: item.createdAt || item.updatedAt || now,
        updatedAt: item.updatedAt || item.createdAt || now,
        updatedBy: item.updatedBy || session?.name || "",
      };
    }, [chartAccounts, categoryToAccount, currentFarmId, financialFarmId, session?.name, buildTaxBreakdown, taxRegimes]);

    const ensureFinancialRecordNumbers = React.useCallback((records)=>{
      const baseRecords = Array.isArray(records) ? records.slice() : [];
      const sorted = baseRecords.map((record, index)=>({ record, index, stamp: String(record?.createdAt || record?.updatedAt || index) }))
        .sort((a,b)=> String(a.stamp).localeCompare(String(b.stamp)));
      let changed = false;
      const working = baseRecords.slice();
      sorted.forEach(({ record, index })=>{
        if (!record || record.recordNumber) return;
        const next = buildNextRecordNumber(working.filter(Boolean), { moduleKey:"financial", costCenter:record?.costCenter, costCenterCodeMap });
        working[index] = { ...record, ...next };
        changed = true;
      });
      return changed ? working : baseRecords;
    }, [costCenterCodeMap]);

    const normalizeEntriesCollection = React.useCallback((items) => ensureFinancialRecordNumbers(dedupeBy(
      (Array.isArray(items) ? items : []).map(normalizeEntry),
      (entry) => entry?.clientId || entry?.id || `${entry?.farmId || ""}::${entry?.originModule || ""}::${entry?.originId || ""}::${entry?.description || ""}`
    )), [dedupeBy, normalizeEntry, ensureFinancialRecordNumbers]);

    const buildFinancialProvisionalNumber = React.useCallback((records, entry) => {
      const next = buildNextRecordNumber((Array.isArray(records) ? records : []).filter(Boolean), { moduleKey:"financial", costCenter:entry?.costCenter, costCenterCodeMap });
      return {
        ...next,
        provisionalRecordNumber: appendProvisionalSuffix(next.recordNumber),
        recordNumber: appendProvisionalSuffix(next.recordNumber),
        recordNumberStatus: "provisional",
      };
    }, [costCenterCodeMap]);

    const markFinancialCreatePending = React.useCallback((entry, existingRecords=[]) => {
      const stableId = entry?.id || entry?.clientId || createClientId("fin");
      const numbered = entry?.recordNumber ? entry : { ...entry, ...buildFinancialProvisionalNumber(existingRecords, entry) };
      return markOperationalCreatePending({
        ...numbered,
        id: stableId,
        clientId: entry?.clientId || stableId,
        farmId: entry?.farmId || currentFarmId || financialFarmId || "",
      }, { userName: session?.name || "" });
    }, [buildFinancialProvisionalNumber, currentFarmId, financialFarmId, session?.name]);

    const markFinancialUpdatePending = React.useCallback((entry, operationType="update") => markOperationalUpdatePending({
      ...entry,
      clientId: entry?.clientId || entry?.id || createClientId("fin"),
      farmId: entry?.farmId || currentFarmId || financialFarmId || "",
    }, { userName: session?.name || "", operationType }), [currentFarmId, financialFarmId, session?.name]);

    const persistEntries = React.useCallback((updater) => {
      setEntries((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        return normalizeEntriesCollection(next);
      });
    }, [normalizeEntriesCollection, setEntries]);

    useEffect(() => {
      const normalized = normalizeEntriesCollection(entries);
      const changed = JSON.stringify(normalized) !== JSON.stringify(entries || []);
      if(changed) setEntries(normalized);
    }, [entries, normalizeEntriesCollection, setEntries]);

    useEffect(() => {
      const normalized = normalizeClosingLocks(closingLocks);
      const changed = JSON.stringify(normalized) !== JSON.stringify(Array.isArray(closingLocks) ? closingLocks : []);
      if(changed) setClosingLocks(normalized);
    }, [closingLocks, normalizeClosingLocks, setClosingLocks]);

    const currentClosingLock = React.useMemo(() => {
      const found = (Array.isArray(closingLocks) ? closingLocks : []).find(item=>String(item?.monthKey || "") === String(competenceMonth || ""));
      return found || { monthKey: competenceMonth || today().slice(0,7), mode: "open", notes: "", lockedBy: "" };
    }, [closingLocks, competenceMonth]);

    const setClosingMode = React.useCallback((mode) => {
      const monthKey = String(competenceMonth || today().slice(0,7));
      const actor = session?.name || "";
      setClosingLocks(prev => {
        const base = Array.isArray(prev) ? [...prev] : [];
        const idx = base.findIndex(item=>String(item?.monthKey || "") === monthKey);
        const existing = idx >= 0 ? base[idx] : null;
        const next = {
          id: existing?.id || createClientId("finlock"),
          monthKey,
          mode,
          notes: existing?.notes || "",
          lockedBy: actor,
          createdAt: existing?.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          updatedBy: actor,
        };
        if(idx >= 0) base[idx] = next;
        else base.push(next);
        return normalizeClosingLocks(base);
      });
      syncOnSave("financialClosingLocks");
      show(mode === "open" ? `✅ Competência ${monthLabel(monthKey)} reaberta.` : `✅ Competência ${monthLabel(monthKey)} atualizada para ${mode === "soft" ? "fechamento assistido" : "travamento rígido"}.`);
    }, [competenceMonth, session?.name, setClosingLocks, normalizeClosingLocks, syncOnSave, show]);

    const setClosingNotes = React.useCallback((notes) => {
      const monthKey = String(competenceMonth || today().slice(0,7));
      const actor = session?.name || "";
      setClosingLocks(prev => {
        const base = Array.isArray(prev) ? [...prev] : [];
        const idx = base.findIndex(item=>String(item?.monthKey || "") === monthKey);
        const existing = idx >= 0 ? base[idx] : null;
        const next = {
          id: existing?.id || createClientId("finlock"),
          monthKey,
          mode: existing?.mode || "open",
          notes: String(notes || ""),
          lockedBy: actor,
          createdAt: existing?.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          updatedBy: actor,
        };
        if(idx >= 0) base[idx] = next;
        else base.push(next);
        return normalizeClosingLocks(base);
      });
    }, [competenceMonth, session?.name, setClosingLocks, normalizeClosingLocks]);

    const getClosingLockForMonth = React.useCallback((monthKey) => {
      const found = (Array.isArray(closingLocks) ? closingLocks : []).find(item=>String(item?.monthKey || "") === String(monthKey || ""));
      return found || { monthKey, mode: "open", notes: "", lockedBy: "" };
    }, [closingLocks]);

    const allEntries = React.useMemo(()=> (Array.isArray(entries) ? entries : []).filter(entry => !isDeletedFinancialEntry(entry)).slice().sort(compareFinancialByLastActivity), [entries]);
    const accessibleEntries = React.useMemo(()=> allEntries.filter(e=>canViewCostCenter(session, "financial", e.costCenter || "")), [allEntries, session]);
    const filtered = React.useMemo(()=> accessibleEntries.filter(e => {
      if(ft !== "Todos" && e.type !== ft) return false;
      if(fcc !== "Todos" && e.costCenter !== fcc) return false;
      return true;
    }), [accessibleEntries, ft, fcc]);

    const competenceEntries = React.useMemo(()=> filtered.filter(e => String(e.competenceMonth || monthFromDate(e.date)).slice(0,7) === competenceMonth), [filtered, competenceMonth]);
    const totalIn = accessibleEntries.filter(e => e.type === "Receita").reduce((s, e) => s + toNum(e.amount), 0);
    const totalOut = accessibleEntries.filter(e => e.type === "Despesa").reduce((s, e) => s + toNum(e.amount), 0);
    const balance = totalIn - totalOut;
    const entriesWithValidation = React.useMemo(() => accessibleEntries.map(entry => ({ ...entry, _validation: validateEntry(entry) })), [accessibleEntries, validateEntry]);
    const competenceValidated = React.useMemo(() => competenceEntries.map(entry => ({ ...entry, _validation: validateEntry(entry) })), [competenceEntries, validateEntry]);
    const pendingDocs = entriesWithValidation.filter(e => e._validation.issues.some(msg => msg.includes('Documento/NF')));
    const pendingTaxSetup = entriesWithValidation.filter(e => e._validation.issues.some(msg => msg.includes('tributário')));
    const pendingPayments = entriesWithValidation.filter(e => !PAYMENT_STATUSES.has(String(e.status || "")) && String(e.dueDate || "") <= today() && String(e.status || "") !== "Cancelado");
    const hardIssues = competenceValidated.filter(e => e._validation.issues.length);
    const warningEntries = competenceValidated.filter(e => !e._validation.issues.length && e._validation.warnings.length);

    const recentAutomatic = React.useMemo(() => [...accessibleEntries]
      .filter(e=>e.automatic)
      .sort((a,b)=>String(b.updatedAt||b.createdAt||"").localeCompare(String(a.updatedAt||a.createdAt||"")))
      .slice(0,8), [accessibleEntries]);

    const byCostCenter = React.useMemo(() => {
      const map = new Map();
      for(const entry of competenceEntries){
        const key = entry.costCenter || "Sem centro";
        const prev = map.get(key) || { costCenter:key, receitas:0, despesas:0 };
        if(entry.type === "Receita") prev.receitas += toNum(entry.amount);
        if(entry.type === "Despesa") prev.despesas += toNum(entry.amount);
        map.set(key, prev);
      }
      return Array.from(map.values()).map(item=>({ ...item, saldo:item.receitas-item.despesas })).sort((a,b)=>Math.abs(b.saldo)-Math.abs(a.saldo));
    }, [competenceEntries]);

    const byAccount = React.useMemo(() => {
      const map = new Map();
      for(const entry of competenceEntries){
        const key = `${entry.accountCode || 'sem-código'}::${entry.accountName || entry.category || 'Sem conta'}`;
        const prev = map.get(key) || { accountCode:entry.accountCode || "", accountName:entry.accountName || entry.category || "Sem conta", receitas:0, despesas:0 };
        if(entry.type === "Receita") prev.receitas += toNum(entry.amount);
        if(entry.type === "Despesa") prev.despesas += toNum(entry.amount);
        map.set(key, prev);
      }
      return Array.from(map.values()).map(item=>({ ...item, saldo:item.receitas-item.despesas })).sort((a,b)=>Math.abs(b.saldo)-Math.abs(a.saldo)).slice(0,10);
    }, [competenceEntries]);

    const payrollSummary = React.useMemo(()=>{
      const active = (employees||[]).filter(emp=>String(emp?.status||'Ativo') !== 'Desligado');
      const base = active.reduce((sum, emp)=>sum + Number(emp?.salary||0), 0);
      const charges = payrollCharges.map(item=>({ ...item, value: base * (Number(item?.pct||0)/100) }));
      const totalCharges = charges.reduce((sum, item)=>sum + item.value, 0);
      return { active, activeCount: active.length, base, charges, totalCharges, total: base + totalCharges };
    }, [employees, payrollCharges]);

    const payrollEntriesForMonth = React.useMemo(() => allEntries.filter(e => String(e.originModule || "") === "payroll" && String(e.competenceMonth || "") === competenceMonth), [allEntries, competenceMonth]);

    const defForm = React.useMemo(() => ({
      _new: true,
      type: "Despesa",
      category: FIN_CATS[0],
      accountCode: "",
      accountName: "",
      description: "",
      amount: 0,
      date: today(),
      dueDate: today(),
      paymentDate: "",
      paymentMethod: "",
      documentNumber: "",
      costCenter: COST_CENTERS[0],
      counterCostCenter: "",
      taxModel: taxRegimes?.[0]?.name || "Atual",
      taxBreakdown: buildTaxBreakdown({ taxModel: taxRegimes?.[0]?.name || "Atual", amount:0 }),
      notes: "",
      justificationMissing: "",
      automatic: false,
      originModule: "manual",
      originId: "",
      status: "Lançado",
      competenceMonth: today().slice(0,7),
      metadata: {},
    }), [taxRegimes, buildTaxBreakdown]);

    const formCompetenceLock = React.useMemo(() => getClosingLockForMonth(String(form?.competenceMonth || monthFromDate(form?.date || today())).slice(0,7)), [form, getClosingLockForMonth]);

    function save(){
      if(form?._new && !canCreate){ show("Sem permissão para criar lançamentos.", "error"); return; }
      if(!form?._new && !canEdit){ show("Sem permissão para editar o financeiro.", "error"); return; }
      const lockedMonth = String(form?.competenceMonth || monthFromDate(form?.date || today())).slice(0,7);
      const lockState = getClosingLockForMonth(lockedMonth);
      if(lockState?.mode === "hard"){
        show(`Competência ${monthLabel(lockedMonth)} está com travamento rígido. Reabra no Fechamento para alterar lançamentos.`, "error");
        return;
      }
      const account = chartAccounts.find(acc=>String(acc.code)===String(form.accountCode||"")) || categoryToAccount(form.category, form.type);
      const payload = {
        ...form,
        accountCode: form.accountCode || account?.code || "",
        accountName: form.accountName || account?.name || "",
        taxBreakdown: buildTaxBreakdown(form),
        competenceMonth: form.competenceMonth || monthFromDate(form.date),
      };
      const validation = validateEntry(payload);
      if(validation.issues.length){ show(validation.issues[0], "error"); return; }
      const { _new, ...d } = payload;
      if(_new){
        persistEntries(p => {
          const base = Array.isArray(p) ? p : [];
          const stableId = d.id || d.clientId || createClientId("fin");
          const draft = {
            ...d,
            id: stableId,
            clientId: d.clientId || stableId,
            farmId: currentFarmId || d.farmId || financialFarmId || "",
            ...makeCreateMeta(session?.name || ""),
          };
          return [...base, markFinancialCreatePending(draft, base.filter(Boolean))];
        });
      } else {
        persistEntries(p => p.map(e => String(e.id) === String(d.id)
          ? markFinancialUpdatePending({
              ...e,
              ...d,
              clientId: d.clientId || e.clientId || e.id || d.id,
              farmId: d.farmId || e.farmId || currentFarmId || financialFarmId || "",
              createdAt: d.createdAt || e.createdAt,
              ...makeUpdateMeta(session?.name || ""),
            })
          : e
        ));
      }
      show("✅ Lançamento salvo!");
      setForm(null);
      syncOnSave("financial");
    }

    function generatePayrollMonth(){
      if(!canCreate && !canEdit){ show("Sem permissão para gerar folha.", "error"); return; }
      const month = competenceMonth || today().slice(0,7);
      if(getClosingLockForMonth(month)?.mode === "hard"){
        show(`Competência ${monthLabel(month)} está com travamento rígido.`, "error");
        return;
      }
      const actor = session?.name || "";
      const salaryDate = `${month}-05`;
      const chargeDate = `${month}-20`;
      let created = 0;
      persistEntries(prev => {
        const base = Array.isArray(prev) ? [...prev] : [];
        const next = [...base];
        for(const emp of payrollSummary.active){
          const empId = String(emp?.id || "");
          const originId = `salary:${month}:${empId}`;
          const exists = base.some(entry => String(entry?.originId || "") === originId);
          if(exists) continue;
          {
            const stableId = createClientId("fin");
            next.push(markFinancialCreatePending({
            id: stableId,
            clientId: stableId,
            farmId: currentFarmId || financialFarmId || "",
            automatic: true,
            originModule: "payroll",
            originId,
            type: "Despesa",
            category: "Folha de Pagamento",
            accountCode: "4.1.04",
            accountName: "Folha de Pagamento",
            description: `Folha ${month} · ${emp?.name || 'Colaborador'}`,
            amount: Number(emp?.salary || 0),
            date: salaryDate,
            dueDate: salaryDate,
            paymentDate: "",
            costCenter: emp?.costCenter || emp?.primaryFarmName || "Sede",
            counterCostCenter: "",
            paymentMethod: "",
            documentNumber: "",
            taxModel: taxRegimes?.[0]?.name || "Atual",
            notes: "Gerado automaticamente pelo módulo financeiro.",
            justificationMissing: "",
            status: "Programado",
            competenceMonth: month,
            metadata: { employeeId: empId, employeeName: emp?.name || "" },
            ...makeCreateMeta(actor),
          }, next.filter(Boolean)));
          }
          created += 1;
        }
        for(const charge of payrollSummary.charges){
          const originId = `charge:${month}:${String(charge?.name || '').toLowerCase()}`;
          const exists = base.some(entry => String(entry?.originId || "") === originId);
          if(exists) continue;
          {
            const stableId = createClientId("fin");
            next.push(markFinancialCreatePending({
            id: stableId,
            clientId: stableId,
            farmId: currentFarmId || financialFarmId || "",
            automatic: true,
            originModule: "payroll",
            originId,
            type: "Despesa",
            category: "Folha de Pagamento",
            accountCode: "4.1.05",
            accountName: "Encargos Trabalhistas",
            description: `Encargo ${month} · ${charge?.name || 'Encargo'}`,
            amount: Number(charge?.value || 0),
            date: chargeDate,
            dueDate: chargeDate,
            paymentDate: "",
            costCenter: "Sede",
            counterCostCenter: "",
            paymentMethod: "",
            documentNumber: "",
            taxModel: taxRegimes?.[0]?.name || "Atual",
            notes: `Base estimada da folha para ${month}.`,
            justificationMissing: "",
            status: "Programado",
            competenceMonth: month,
            metadata: { chargeName: charge?.name || "", pct: Number(charge?.pct || 0) },
            ...makeCreateMeta(actor),
          }, next.filter(Boolean)));
          }
          created += 1;
        }
        return next;
      });
      syncOnSave("financial");
      show(created ? `✅ Folha de ${monthLabel(month)} gerada com ${created} lançamentos.` : `ℹ️ A folha de ${monthLabel(month)} já havia sido gerada.`);
    }

    const monthTaxes = React.useMemo(() => {
      const map = new Map();
      for(const entry of competenceValidated){
        const taxes = Array.isArray(entry?.taxBreakdown) ? entry.taxBreakdown.filter(t=>t?.included !== false && String(t?.name||"").trim()) : [];
        for(const tax of taxes){
          const key = String(tax?.name || "");
          const prev = map.get(key) || { name:key, count:0, totalBase:0, totalTax:0 };
          prev.count += 1;
          prev.totalBase += Number(entry.amount || 0);
          prev.totalTax += Number(tax?.amount || 0);
          map.set(key, prev);
        }
      }
      return Array.from(map.values()).sort((a,b)=>b.totalTax-a.totalTax || b.totalBase-a.totalBase);
    }, [competenceValidated]);

    const closingSummary = React.useMemo(() => {
      const summary = {
        entries: competenceValidated.length,
        hardIssues: hardIssues.length,
        warnings: warningEntries.length,
        withDocument: competenceValidated.filter(e=>String(e.documentNumber || "").trim()).length,
        withTaxes: competenceValidated.filter(e=>Array.isArray(e.taxBreakdown) && e.taxBreakdown.some(t=>t?.included !== false && (Number(t?.amount||0) > 0 || Number(t?.pct||0) > 0))).length,
        paidOrReceived: competenceValidated.filter(e=>PAYMENT_STATUSES.has(String(e.status||""))).length,
        ready: competenceValidated.length > 0 && hardIssues.length === 0,
        lockMode: currentClosingLock?.mode || 'open',
        lockNotes: currentClosingLock?.notes || '',
        canStrictClose: competenceValidated.length > 0 && hardIssues.length === 0,
      };
      return summary;
    }, [competenceValidated, hardIssues, warningEntries, currentClosingLock]);

    const reconciliationEntries = React.useMemo(() => competenceValidated.map(entry => {
      const status = String(entry?.status || "");
      const settled = PAYMENT_STATUSES.has(status) && String(entry?.paymentDate || "").trim();
      const hasDocumentOrJustification = !!String(entry?.documentNumber || entry?.justificationMissing || "").trim();
      const overdue = !PAYMENT_STATUSES.has(status) && String(entry?.dueDate || "") && String(entry?.dueDate || "") < today() && status !== "Cancelado";
      const conciliated = !!(settled && hasDocumentOrJustification && !entry?._validation?.issues?.length);
      return {
        ...entry,
        _reconciliation: {
          settled,
          overdue,
          hasDocumentOrJustification,
          conciliated,
          statusLabel: conciliated ? 'Conciliado' : (overdue ? 'Vencido' : (settled ? 'Liquidado pendente' : 'Em aberto')),
        },
      };
    }), [competenceValidated]);

    const reconciliationSummary = React.useMemo(() => ({
      conciliated: reconciliationEntries.filter(e=>e._reconciliation.conciliated).length,
      settledPending: reconciliationEntries.filter(e=>e._reconciliation.settled && !e._reconciliation.conciliated).length,
      open: reconciliationEntries.filter(e=>!e._reconciliation.settled && !e._reconciliation.overdue && String(e.status || '') !== 'Cancelado').length,
      overdue: reconciliationEntries.filter(e=>e._reconciliation.overdue).length,
      missingDocOnSettled: reconciliationEntries.filter(e=>e._reconciliation.settled && !e._reconciliation.hasDocumentOrJustification).length,
    }), [reconciliationEntries]);

    const reportByOrigin = React.useMemo(() => {
      const map = new Map();
      for(const entry of competenceEntries){
        const key = String(entry?.originModule || 'manual') || 'manual';
        const prev = map.get(key) || { originModule:key, count:0, receitas:0, despesas:0, automatic:0 };
        prev.count += 1;
        if(entry.type === 'Receita') prev.receitas += toNum(entry.amount);
        if(entry.type === 'Despesa') prev.despesas += toNum(entry.amount);
        if(entry.automatic) prev.automatic += 1;
        map.set(key, prev);
      }
      return Array.from(map.values()).map(item=>({ ...item, saldo:item.receitas-item.despesas })).sort((a,b)=>Math.abs(b.receitas + b.despesas)-Math.abs(a.receitas + a.despesas));
    }, [competenceEntries]);

    const cashFlowTimeline = React.useMemo(() => {
      const map = new Map();
      for(const entry of competenceEntries){
        const refDate = String(entry?.paymentDate || entry?.date || entry?.dueDate || '').slice(0,10);
        if(!refDate) continue;
        const prev = map.get(refDate) || { date:refDate, receitas:0, despesas:0, qty:0 };
        prev.qty += 1;
        if(entry.type === 'Receita') prev.receitas += toNum(entry.amount);
        if(entry.type === 'Despesa') prev.despesas += toNum(entry.amount);
        map.set(refDate, prev);
      }
      return Array.from(map.values()).map(item=>({ ...item, saldo:item.receitas-item.despesas })).sort((a,b)=>String(a.date).localeCompare(String(b.date)));
    }, [competenceEntries]);

    const stockItemMap = React.useMemo(() => new Map((Array.isArray(stockItems) ? stockItems : []).map(item=>[String(item?.id || ""), item])), [stockItems]);

    const stockLedger = React.useMemo(() => {
      const start = monthStart(competenceMonth);
      const end = monthEnd(competenceMonth);
      return (Array.isArray(stockMovements) ? stockMovements : [])
        .filter(mov => {
          const date = String((mov?.ts || mov?.date || mov?.createdAt || "")).slice(0,10);
          if(!date) return false;
          if(date < start || date > end) return false;
          const type = String(mov?.type || "");
          return ["saida", "descarte", "transferencia", "ajuste", "nfe", "entrada"].includes(type);
        })
        .map(mov => {
          const item = stockItemMap.get(String(mov?.itemId || "")) || {};
          const qty = Number(mov?.qty || 0);
          const unitCost = Number(mov?.unitCost || item?.unitCost || 0);
          const total = qty * unitCost;
          const type = String(mov?.type || "");
          const delta = String(mov?.delta || "");
          const sourceCostCenter = mov?.sourceCostCenter || (type === "transferencia" && delta === "entrada" ? mov?.sourceCostCenter || "" : mov?.costCenter || "");
          const destCostCenter = mov?.destCostCenter || (type === "transferencia" && delta === "saida" ? mov?.destCostCenter || "" : mov?.costCenter || "");
          let kind = "Ajuste";
          if(type === "nfe" || type === "entrada") kind = "Entrada";
          else if(type === "descarte") kind = "Descarte";
          else if(type === "saida") kind = "Consumo";
          else if(type === "transferencia") kind = "Transferência";
          return {
            id: String(mov?.id || `${type}:${mov?.itemId || ''}:${mov?.ts || ''}`),
            date: String(mov?.ts || mov?.date || mov?.createdAt || "").slice(0,10),
            itemName: item?.name || "Item sem nome",
            itemUnit: item?.unit || "un",
            category: item?.category || "",
            qty,
            unitCost,
            total,
            type,
            delta,
            kind,
            costCenter: mov?.costCenter || item?.costCenter || "Sede",
            sourceCostCenter: sourceCostCenter || "",
            destCostCenter: destCostCenter || "",
            notes: mov?.notes || "",
            by: mov?.by || "",
          };
        })
        .sort((a,b)=>String(b.date || "").localeCompare(String(a.date || "")));
    }, [stockMovements, stockItemMap, competenceMonth]);

    const stockConsumptionEntries = React.useMemo(() => stockLedger.filter(entry => entry.kind === "Consumo" || entry.kind === "Descarte"), [stockLedger]);

    const stockTransfers = React.useMemo(() => stockLedger.filter(entry => entry.kind === "Transferência"), [stockLedger]);

    const stockByCostCenter = React.useMemo(() => {
      const map = new Map();
      for(const entry of stockConsumptionEntries){
        const key = entry.costCenter || "Sem centro";
        const prev = map.get(key) || { costCenter:key, consumption:0, discard:0, transferOut:0, transferIn:0, moves:0 };
        if(entry.kind === "Descarte") prev.discard += Number(entry.total || 0);
        else prev.consumption += Number(entry.total || 0);
        prev.moves += 1;
        map.set(key, prev);
      }
      for(const entry of stockTransfers){
        if(entry.delta === 'saida'){
          const key = entry.sourceCostCenter || entry.costCenter || "Sem centro";
          const prev = map.get(key) || { costCenter:key, consumption:0, discard:0, transferOut:0, transferIn:0, moves:0 };
          prev.transferOut += Number(entry.total || 0);
          prev.moves += 1;
          map.set(key, prev);
        }
        if(entry.delta === 'entrada'){
          const key = entry.destCostCenter || entry.costCenter || "Sem centro";
          const prev = map.get(key) || { costCenter:key, consumption:0, discard:0, transferOut:0, transferIn:0, moves:0 };
          prev.transferIn += Number(entry.total || 0);
          prev.moves += 1;
          map.set(key, prev);
        }
      }
      return Array.from(map.values())
        .map(item => ({ ...item, totalImpact: Number(item.consumption || 0) + Number(item.discard || 0) }))
        .sort((a,b)=> (b.totalImpact + b.transferOut + b.transferIn) - (a.totalImpact + a.transferOut + a.transferIn));
    }, [stockConsumptionEntries, stockTransfers]);

    const stockTopItems = React.useMemo(() => {
      const map = new Map();
      for(const entry of stockConsumptionEntries){
        const key = entry.itemName;
        const prev = map.get(key) || { itemName:key, qty:0, total:0, unit:entry.itemUnit, category:entry.category };
        prev.qty += Number(entry.qty || 0);
        prev.total += Number(entry.total || 0);
        map.set(key, prev);
      }
      return Array.from(map.values()).sort((a,b)=>b.total-a.total).slice(0,10);
    }, [stockConsumptionEntries]);

    const stockTotals = React.useMemo(() => ({
      consumption: stockConsumptionEntries.filter(e=>e.kind === 'Consumo').reduce((sum, entry)=>sum + Number(entry.total || 0), 0),
      discard: stockConsumptionEntries.filter(e=>e.kind === 'Descarte').reduce((sum, entry)=>sum + Number(entry.total || 0), 0),
      transferOut: stockTransfers.filter(e=>e.delta === 'saida').reduce((sum, entry)=>sum + Number(entry.total || 0), 0),
      transferIn: stockTransfers.filter(e=>e.delta === 'entrada').reduce((sum, entry)=>sum + Number(entry.total || 0), 0),
    }), [stockConsumptionEntries, stockTransfers]);



    const efficiencyByCostCenter = React.useMemo(() => {
      const map = new Map();
      const supportExpenseCats = ["tribut", "impost", "financeira", "juros"];
      for(const entry of competenceEntries){
        const key = String(entry?.costCenter || "Sem centro");
        const prev = map.get(key) || { costCenter:key, receitas:0, despesas:0, operacionalReceita:0, operacionalDespesa:0, count:0 };
        prev.count += 1;
        const amount = toNum(entry.amount);
        const categoryText = `${entry?.category || ''} ${entry?.accountName || ''}`.toLowerCase();
        if(entry.type === 'Receita'){
          prev.receitas += amount;
          prev.operacionalReceita += amount;
        }
        if(entry.type === 'Despesa'){
          prev.despesas += amount;
          if(!supportExpenseCats.some(token=>categoryText.includes(token))) prev.operacionalDespesa += amount;
        }
        map.set(key, prev);
      }
      for(const stock of stockByCostCenter){
        const key = String(stock?.costCenter || 'Sem centro');
        const prev = map.get(key) || { costCenter:key, receitas:0, despesas:0, operacionalReceita:0, operacionalDespesa:0, count:0 };
        prev.operacionalDespesa += Number(stock?.consumption || 0) + Number(stock?.discard || 0);
        map.set(key, prev);
      }
      return Array.from(map.values()).map(item=>{
        const totalDespesaRef = item.operacionalDespesa || item.despesas;
        const efficiencyPct = totalDespesaRef > 0 ? Number((((item.operacionalReceita - totalDespesaRef) / totalDespesaRef) * 100).toFixed(1)) : (item.operacionalReceita > 0 ? 100 : 0);
        const coveragePct = totalDespesaRef > 0 ? Number(((item.operacionalReceita / totalDespesaRef) * 100).toFixed(1)) : 0;
        let rating = 'Atenção';
        let color = T.orange || T.accent;
        if(coveragePct >= 120){ rating = 'Alta eficiência'; color = T.green; }
        else if(coveragePct >= 95){ rating = 'Equilibrado'; color = T.accent; }
        else if(item.operacionalReceita === 0 && totalDespesaRef > 0){ rating = 'Centro consumidor'; color = T.red; }
        return { ...item, totalDespesaRef, efficiencyPct, coveragePct, rating, color, margem:item.operacionalReceita - totalDespesaRef };
      }).sort((a,b)=>b.coveragePct - a.coveragePct || b.operacionalReceita - a.operacionalReceita);
    }, [competenceEntries, stockByCostCenter]);

    const dreSummary = React.useMemo(() => {
      const result = {
        receitaBruta: 0,
        deducoes: 0,
        receitaLiquida: 0,
        custoEstoqueConsumido: 0,
        custoFolha: 0,
        custoServicosTerceiros: 0,
        despesasOperacionais: 0,
        resultadoOperacional: 0,
        resultadoLiquido: 0,
      };
      for(const entry of competenceEntries){
        const amount = toNum(entry.amount);
        const text = `${entry?.category || ''} ${entry?.accountName || ''} ${entry?.description || ''}`.toLowerCase();
        if(entry.type === 'Receita') result.receitaBruta += amount;
        if(entry.type === 'Despesa'){
          if(text.includes('tribut') || text.includes('impost')) result.deducoes += amount;
          else if(text.includes('folha') || text.includes('encargo')) result.custoFolha += amount;
          else if(text.includes('terceir')) result.custoServicosTerceiros += amount;
          else result.despesasOperacionais += amount;
        }
      }
      result.custoEstoqueConsumido = Number(stockTotals.consumption || 0) + Number(stockTotals.discard || 0);
      result.receitaLiquida = result.receitaBruta - result.deducoes;
      result.resultadoOperacional = result.receitaLiquida - result.custoEstoqueConsumido - result.custoFolha - result.custoServicosTerceiros - result.despesasOperacionais;
      result.resultadoLiquido = result.resultadoOperacional;
      return result;
    }, [competenceEntries, stockTotals]);

    const homeCardIds = React.useMemo(()=>["receitas","despesas","saldo","consumo_estoque_rs","resultado_operacional","descartes"], []);
    const entriesCardIds = React.useMemo(()=>["lancamentos_total","lancamentos_receitas","lancamentos_despesas","lancamentos_bloqueios"], []);
    const payrollCardIds = React.useMemo(()=>["folha_colaboradores_ativos","folha_base_salarial","folha_encargos","folha_custo_estimado"], []);
    const stockCardIds = React.useMemo(()=>["consumo_estoque_rs","descartes","transferencias_saida","transferencias_entrada"], []);
    const dreCardIds = React.useMemo(()=>["receita_bruta","deducoes_tributos","custo_estoque","resultado_operacional"], []);
    const pendingCardIds = React.useMemo(()=>["pendencias_documento","pendencias_vencidas","problemas_tributarios","alertas_fechamento"], []);

    const allowedHomeCards = React.useMemo(()=>getConfiguredSectionCards("moduleHome", homeCardIds), [getConfiguredSectionCards, homeCardIds]);
    const allowedEntriesCards = React.useMemo(()=>getConfiguredSectionCards("menu:entries", entriesCardIds), [getConfiguredSectionCards, entriesCardIds]);
    const allowedPayrollCards = React.useMemo(()=>getConfiguredSectionCards("menu:payroll", payrollCardIds), [getConfiguredSectionCards, payrollCardIds]);
    const reconciliationCardIds = React.useMemo(()=>["conciliados","em_aberto","vencidos","liquidados_pendentes"], []);
    const reportsCardIds = React.useMemo(()=>["contas_ativas","centros_ativos","origens_automaticas","dias_fluxo"], []);
    const closingCardIds = React.useMemo(()=>["fechamento_lancamentos","fechamento_bloqueios","fechamento_avisos","fechamento_status"], []);

    const allowedStockCards = React.useMemo(()=>getConfiguredSectionCards("menu:stock", stockCardIds), [getConfiguredSectionCards, stockCardIds]);
    const allowedReconciliationCards = React.useMemo(()=>getConfiguredSectionCards("menu:reconciliation", reconciliationCardIds), [getConfiguredSectionCards, reconciliationCardIds]);
    const allowedReportsCards = React.useMemo(()=>getConfiguredSectionCards("menu:reports", reportsCardIds), [getConfiguredSectionCards, reportsCardIds]);
    const allowedDreCards = React.useMemo(()=>getConfiguredSectionCards("menu:dre", dreCardIds), [getConfiguredSectionCards, dreCardIds]);
    const allowedPendingCards = React.useMemo(()=>getConfiguredSectionCards("menu:pending", pendingCardIds), [getConfiguredSectionCards, pendingCardIds]);
    const allowedClosingCards = React.useMemo(()=>getConfiguredSectionCards("menu:closing", closingCardIds), [getConfiguredSectionCards, closingCardIds]);

    const renderTopCards = React.useCallback((cards) => {
      const visibleCards = Array.isArray(cards) ? cards.filter(Boolean) : [];
      if(!visibleCards.length) return null;
      return <div style={{display:"grid",gridTemplateColumns:"repeat(2, minmax(0, 1fr))",gap:14,marginBottom:18}}>{visibleCards}</div>;
    }, []);

    const issueBuckets = React.useMemo(() => {
      const map = new Map();
      for(const entry of competenceValidated){
        for(const issue of entry._validation.issues){
          const prev = map.get(issue) || { label: issue, count: 0 };
          prev.count += 1;
          map.set(issue, prev);
        }
      }
      return Array.from(map.values()).sort((a,b)=>b.count-a.count);
    }, [competenceValidated]);

    const currentTopCards = React.useMemo(() => {
      if(tab === "dashboard") return renderTopCards([
        allowedHomeCards.includes("receitas") ? <StatCard key="receitas" icon="📈" label="Receitas" value={`R$ ${fmtMoney(totalIn)}`} color={T.green}/> : null,
        allowedHomeCards.includes("despesas") ? <StatCard key="despesas" icon="📉" label="Despesas" value={`R$ ${fmtMoney(totalOut)}`} color={T.red}/> : null,
        allowedHomeCards.includes("saldo") ? <StatCard key="saldo" icon="💰" label="Saldo" value={`R$ ${fmtMoney(Math.abs(balance))}`} color={balance>=0?T.green:T.red} sub={balance<0?"Negativo":"Positivo"}/> : null,
        allowedHomeCards.includes("consumo_estoque_rs") ? <StatCard key="consumo_estoque_rs" icon="📦" label="Consumo Estoque R$" value={`R$ ${fmtMoney(stockTotals.consumption)}`} color={T.red} sub={monthLabel(competenceMonth)}/> : null,
        allowedHomeCards.includes("resultado_operacional") ? <StatCard key="resultado_operacional" icon="🏁" label="Resultado Operacional" value={`R$ ${fmtMoney(Math.abs(dreSummary.resultadoOperacional))}`} color={dreSummary.resultadoOperacional>=0?T.green:T.red} sub={dreSummary.resultadoOperacional>=0?"Positivo":"Negativo"}/> : null,
        allowedHomeCards.includes("descartes") ? <StatCard key="descartes" icon="🗑️" label="Descartes" value={`R$ ${fmtMoney(stockTotals.discard)}`} color={T.orange || T.accent} sub={`${stockConsumptionEntries.filter(e=>e.kind==="Descarte").length} mov.`}/> : null,
      ]);
      if(tab === "entries") return renderTopCards([
        allowedEntriesCards.includes("lancamentos_total") ? <StatCard key="lancamentos_total" icon="🧾" label="Lançamentos" value={competenceValidated.length} color={T.accent} sub={monthLabel(competenceMonth)}/> : null,
        allowedEntriesCards.includes("lancamentos_receitas") ? <StatCard key="lancamentos_receitas" icon="📈" label="Receitas" value={`R$ ${fmtMoney(competenceEntries.filter(e=>e.type==='Receita').reduce((s,e)=>s+toNum(e.amount),0))}`} color={T.green}/> : null,
        allowedEntriesCards.includes("lancamentos_despesas") ? <StatCard key="lancamentos_despesas" icon="📉" label="Despesas" value={`R$ ${fmtMoney(competenceEntries.filter(e=>e.type==='Despesa').reduce((s,e)=>s+toNum(e.amount),0))}`} color={T.red}/> : null,
        allowedEntriesCards.includes("lancamentos_bloqueios") ? <StatCard key="lancamentos_bloqueios" icon="⚠️" label="Bloqueios" value={hardIssues.length} color={T.orange || T.accent} sub={`${warningEntries.length} aviso(s)`}/> : null,
      ]);
      if(tab === "payroll") return renderTopCards([
        allowedPayrollCards.includes("folha_colaboradores_ativos") ? <StatCard key="folha_colaboradores_ativos" icon="👥" label="Colaboradores Ativos" value={payrollSummary.activeCount} color={T.accent} sub={monthLabel(competenceMonth)}/> : null,
        allowedPayrollCards.includes("folha_base_salarial") ? <StatCard key="folha_base_salarial" icon="💵" label="Base Salarial" value={`R$ ${fmtMoney(payrollSummary.base)}`} color={T.green}/> : null,
        allowedPayrollCards.includes("folha_encargos") ? <StatCard key="folha_encargos" icon="🧾" label="Encargos" value={`R$ ${fmtMoney(payrollSummary.totalCharges)}`} color={T.orange || T.accent}/> : null,
        allowedPayrollCards.includes("folha_custo_estimado") ? <StatCard key="folha_custo_estimado" icon="🏁" label="Custo Estimado" value={`R$ ${fmtMoney(payrollSummary.total)}`} color={T.red}/> : null,
      ]);
      if(tab === "stock") return renderTopCards([
        allowedStockCards.includes("consumo_estoque_rs") ? <StatCard key="consumo_estoque_rs" icon="📦" label="Consumo Estoque R$" value={`R$ ${fmtMoney(stockTotals.consumption)}`} color={T.red} sub={monthLabel(competenceMonth)}/> : null,
        allowedStockCards.includes("descartes") ? <StatCard key="descartes" icon="🗑️" label="Descartes" value={`R$ ${fmtMoney(stockTotals.discard)}`} color={T.orange || T.accent} sub={`${stockConsumptionEntries.filter(e=>e.kind==='Descarte').length} mov.`}/> : null,
        allowedStockCards.includes("transferencias") ? <StatCard key="transferencias" icon="🔁" label="Transferências" value={`R$ ${fmtMoney(stockTotals.transferImpact)}`} color={T.accent} sub={`${stockConsumptionEntries.filter(e=>e.kind==='Transferência').length} mov.`}/> : null,
        allowedStockCards.includes("itens_consumidos") ? <StatCard key="itens_consumidos" icon="📋" label="Itens consumidos" value={stockTopItems.length} color={T.green} sub="Top da competência"/> : null,
      ]);
      if(tab === "reconciliation") return renderTopCards([
        allowedReconciliationCards.includes("conciliados") ? <StatCard key="conciliados" icon="✅" label="Conciliados" value={reconciliationSummary.conciliated} color={T.green} sub={monthLabel(competenceMonth)}/> : null,
        allowedReconciliationCards.includes("em_aberto") ? <StatCard key="em_aberto" icon="🕓" label="Em aberto" value={reconciliationSummary.open} color={T.accent} sub="ainda não liquidados"/> : null,
        allowedReconciliationCards.includes("vencidos") ? <StatCard key="vencidos" icon="⚠️" label="Vencidos" value={reconciliationSummary.overdue} color={T.red} sub="exigem ação"/> : null,
        allowedReconciliationCards.includes("liquidados_pendentes") ? <StatCard key="liquidados_pendentes" icon="📄" label="Liquidados pendentes" value={reconciliationSummary.settledPending} color={T.orange || T.accent} sub="faltando conciliação"/> : null,
      ]);
      if(tab === "reports") return renderTopCards([
        allowedReportsCards.includes("contas_ativas") ? <StatCard key="contas_ativas" icon="📚" label="Contas ativas" value={byAccount.length} color={T.accent} sub={monthLabel(competenceMonth)}/> : null,
        allowedReportsCards.includes("centros_ativos") ? <StatCard key="centros_ativos" icon="🏷️" label="Centros ativos" value={byCostCenter.length} color={T.green} sub="com movimentação"/> : null,
        allowedReportsCards.includes("origens_automaticas") ? <StatCard key="origens_automaticas" icon="🤖" label="Origens automáticas" value={reportByOrigin.filter(item=>item.automatic).length} color={T.orange || T.accent} sub="integrações"/> : null,
        allowedReportsCards.includes("dias_fluxo") ? <StatCard key="dias_fluxo" icon="🗓️" label="Dias com fluxo" value={cashFlowTimeline.length} color={T.red} sub="na competência"/> : null,
      ]);
      if(tab === "dre") return renderTopCards([
        allowedDreCards.includes("receita_bruta") ? <StatCard key="receita_bruta" icon="💵" label="Receita bruta" value={`R$ ${fmtMoney(dreSummary.receitaBruta)}`} color={T.green} sub={monthLabel(competenceMonth)}/> : null,
        allowedDreCards.includes("deducoes_tributos") ? <StatCard key="deducoes_tributos" icon="🧾" label="Deduções / tributos" value={`R$ ${fmtMoney(dreSummary.deducoes)}`} color={T.orange || T.accent} sub="Impostos e deduções"/> : null,
        allowedDreCards.includes("custo_estoque") ? <StatCard key="custo_estoque" icon="📦" label="Custo de estoque" value={`R$ ${fmtMoney(dreSummary.custoEstoqueConsumido)}`} color={T.red} sub="Consumo + descarte"/> : null,
        allowedDreCards.includes("resultado_operacional") ? <StatCard key="resultado_operacional" icon="🏁" label="Resultado operacional" value={`R$ ${fmtMoney(Math.abs(dreSummary.resultadoOperacional))}`} color={dreSummary.resultadoOperacional>=0?T.green:T.red} sub={dreSummary.resultadoOperacional>=0?"Positivo":"Negativo"}/> : null,
      ]);
      if(tab === "pending") return renderTopCards([
        allowedPendingCards.includes("pendencias_documento") ? <StatCard key="pendencias_documento" icon="📄" label="Pendências de documento" value={pendingDocs.length} color={T.orange || T.accent} sub="exigem ação"/> : null,
        allowedPendingCards.includes("pendencias_vencidas") ? <StatCard key="pendencias_vencidas" icon="🕓" label="Pendências vencidas" value={pendingPayments.length} color={T.red} sub="em aberto"/> : null,
        allowedPendingCards.includes("problemas_tributarios") ? <StatCard key="problemas_tributarios" icon="🧾" label="Problemas tributários" value={pendingTaxSetup.length} color={T.accent} sub="sem detalhamento"/> : null,
        allowedPendingCards.includes("alertas_fechamento") ? <StatCard key="alertas_fechamento" icon="⚠️" label="Alertas de fechamento" value={hardIssues.length + warningEntries.length} color={T.orange || T.accent} sub={`${hardIssues.length} bloqueio(s)`}/> : null,
      ]);
      if(tab === "closing") return renderTopCards([
        allowedClosingCards.includes("fechamento_lancamentos") ? <StatCard key="fechamento_lancamentos" icon="🧾" label="Lançamentos" value={closingSummary.entries} color={T.accent} sub={monthLabel(competenceMonth)}/> : null,
        allowedClosingCards.includes("fechamento_bloqueios") ? <StatCard key="fechamento_bloqueios" icon="⛔" label="Bloqueios" value={closingSummary.hardIssues} color={T.red} sub="impedem travamento"/> : null,
        allowedClosingCards.includes("fechamento_avisos") ? <StatCard key="fechamento_avisos" icon="⚠️" label="Avisos" value={closingSummary.warningIssues} color={T.orange || T.accent} sub="revisar antes de fechar"/> : null,
        allowedClosingCards.includes("fechamento_status") ? <StatCard key="fechamento_status" icon="🗂️" label="Status" value={currentClosingLock.mode === 'hard' ? 'Rígido' : (currentClosingLock.mode === 'soft' ? 'Assistido' : 'Aberta')} color={currentClosingLock.mode === 'hard' ? T.red : (currentClosingLock.mode === 'soft' ? (T.orange || T.accent) : T.green)} sub={currentClosingLock.mode === 'hard' ? 'Travada' : (currentClosingLock.mode === 'soft' ? 'Acompanha pendências' : 'Sem trava')} /> : null,
      ]);
      return null;
    }, [
      allowedClosingCards, allowedDreCards, allowedEntriesCards, allowedHomeCards, allowedPayrollCards,
      allowedPendingCards, allowedReconciliationCards, allowedReportsCards, allowedStockCards,
      balance, closingSummary, competenceEntries, competenceMonth, competenceValidated, currentClosingLock,
      dreSummary, hardIssues.length, pendingDocs.length, pendingPayments.length,
      pendingTaxSetup.length, payrollSummary, reconciliationSummary, renderTopCards, reportByOrigin,
      cashFlowTimeline.length, stockConsumptionEntries, stockTopItems.length, stockTotals, tab,
      totalIn, totalOut, warningEntries.length, byAccount.length, byCostCenter.length,
    ]);

    const openFinancialForm = React.useCallback((entry)=>{
      if(!entry) return;
      setForm({...entry,_new:false,taxBreakdown:buildTaxBreakdown(entry)});
    }, [buildTaxBreakdown]);

    return <div><Toast toast={toast}/>

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10,marginBottom:14}}>
        <ModuleHeaderMenu
          T={T}
          title="Menu do Financeiro"
          items={[
            { id:"dashboard", label:"Visão geral", icon:"📊", active:tab==="dashboard", onClick:()=>setTab("dashboard") },
            { id:"entries", label:"Lançamentos", icon:"🧾", badge:filtered.length, active:tab==="entries", onClick:()=>setTab("entries") },
            { id:"payroll", label:"Folha", icon:"👥", badge:payrollEntriesForMonth.length, active:tab==="payroll", onClick:()=>setTab("payroll") },
            { id:"stock", label:"Estoque", icon:"📦", badge:stockLedger.length, active:tab==="stock", onClick:()=>setTab("stock") },
            { id:"reconciliation", label:"Conciliação", icon:"🔗", badge:reconciliationSummary.overdue + reconciliationSummary.settledPending, active:tab==="reconciliation", onClick:()=>setTab("reconciliation") },
            { id:"reports", label:"Relatórios", icon:"📈", badge:reportByOrigin.length, active:tab==="reports", onClick:()=>setTab("reports") },
            { id:"dre", label:"DRE", icon:"📑", badge:1, active:tab==="dre", onClick:()=>setTab("dre") },
            { id:"pending", label:"Rastreio", icon:"📌", badge:pendingDocs.length + pendingPayments.length, active:tab==="pending", onClick:()=>setTab("pending") },
            { id:"closing", label:"Fechamento", icon:"🗂️", badge:hardIssues.length, active:tab==="closing", onClick:()=>setTab("closing") },
          ]}
        />
      </div>

      {currentTopCards}

      <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center",marginBottom:14}}>
        <Input type="month" value={competenceMonth} onChange={e=>setCompetenceMonth(e.target.value)} style={{width:isCompactViewport()?"100%":"180px"}}/>
        {canCreate && <Btn onClick={()=>setForm({...defForm, costCenter:allowedCostCenters[0] || defForm.costCenter, _new:true})}>+ Lançamento</Btn>}
      </div>

      {deniedCostCenters.length>0 && <div style={{marginBottom:14,padding:12,borderRadius:12,border:`1px solid ${T.orange || T.accent}`,background:`${T.orange || T.accent}12`,color:T.text,fontSize:12}}>Você não tem acesso financeiro aos centros de custo: <strong>{deniedCostCenters.join(", ")}</strong>. Nesses pontos o sistema mostra <strong>ND</strong>.</div>}
      {tab === "dashboard" && <>
        <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1.15fr .85fr",gap:14,marginBottom:18}}>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,marginBottom:10,flexWrap:"wrap"}}>
              <div>
                <div style={{fontWeight:800,color:T.text}}>Plano contábil e rastreamento</div>
                <div style={{fontSize:12,color:T.muted}}>Base preparada para operação no modelo atual e também para transição do IVA dual.</div>
              </div>
              <Badge color={T.accent}>{monthLabel(competenceMonth)}</Badge>
            </div>
            <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:10}}>
              {chartAccounts.slice(0,8).map(acc=><Badge key={acc.code} color={acc.type==="Receita"?T.green:(acc.type==="Despesa"?T.red:T.accent)}>{acc.code} · {acc.name}</Badge>)}
            </div>
            <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
              {taxRegimes.map(regime=><Badge key={regime?.name || JSON.stringify(regime)} color={T.purple || T.accent}>{regime?.name}{Array.isArray(regime?.taxes) && regime.taxes.length ? ` · ${regime.taxes.join(', ')}` : ''}</Badge>)}
            </div>
          </div>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14}}>
            <div style={{fontWeight:800,color:T.text,marginBottom:8}}>Competência {monthLabel(competenceMonth)}</div>
            <div style={{fontSize:12,color:T.text}}>Receitas: <strong>R$ {fmtMoney(competenceEntries.filter(e=>e.type==='Receita').reduce((s,e)=>s+toNum(e.amount),0))}</strong></div>
            <div style={{fontSize:12,color:T.text}}>Despesas: <strong>R$ {fmtMoney(competenceEntries.filter(e=>e.type==='Despesa').reduce((s,e)=>s+toNum(e.amount),0))}</strong></div>
            <div style={{fontSize:13,color:T.green,fontWeight:800,marginTop:6}}>Saldo: R$ {fmtMoney(competenceEntries.reduce((s,e)=>s + (e.type==='Receita'?toNum(e.amount):-toNum(e.amount)),0))}</div>
            <div style={{fontSize:12,color:T.muted,marginTop:8}}>Base de tributos rastreados: {monthTaxes.length} tributos ativos na competência.</div>
          </div>
        </div>

        <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1.1fr .9fr",gap:14,marginBottom:18}}>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14}}>
            <div style={{fontWeight:800,color:T.text,marginBottom:10}}>Consumo e transferências por centro de custo</div>
            {stockByCostCenter.length ? stockByCostCenter.map(item=><div key={item.costCenter} style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr auto",gap:8,padding:"9px 0",borderBottom:`1px solid ${T.border}`}}>
              <div>
                <div style={{fontWeight:700,color:T.text}}>{item.costCenter}</div>
                <div style={{fontSize:11,color:T.muted}}>Consumo R$ {fmtMoney(item.consumption || 0)} · Descarte R$ {fmtMoney(item.discard || 0)} · Saída R$ {fmtMoney(item.transferOut || 0)} · Entrada R$ {fmtMoney(item.transferIn || 0)}</div>
              </div>
              <div style={{fontWeight:800,color:T.text}}>R$ {fmtMoney(item.totalImpact || 0)}</div>
            </div>) : <div style={{fontSize:12,color:T.muted}}>Sem movimentações de estoque na competência.</div>}
          </div>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14}}>
            <div style={{fontWeight:800,color:T.text,marginBottom:10}}>Itens mais consumidos do estoque</div>
            {stockTopItems.length ? stockTopItems.map(item=><div key={item.itemName} style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr auto",gap:8,padding:"9px 0",borderBottom:`1px solid ${T.border}`}}>
              <div>
                <div style={{fontWeight:700,color:T.text}}>{item.itemName}</div>
                <div style={{fontSize:11,color:T.muted}}>{item.category || 'Sem categoria'} · {item.qty.toLocaleString('pt-BR')} {item.unit || 'un'}</div>
              </div>
              <div style={{fontWeight:800,color:T.red}}>R$ {fmtMoney(item.total || 0)}</div>
            </div>) : <div style={{fontSize:12,color:T.muted}}>Sem consumo apurado na competência.</div>}
          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:14,marginBottom:18}}>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14}}>
            <div style={{fontWeight:800,color:T.text,marginBottom:10}}>Fechamento por centro de custo</div>
            {byCostCenter.length ? byCostCenter.map(item=><div key={item.costCenter} style={{display:"grid",gridTemplateColumns:"1fr auto",gap:8,padding:"9px 0",borderBottom:`1px solid ${T.border}`}}>
              <div>
                <div style={{fontWeight:700,color:T.text}}>{item.costCenter}</div>
                <div style={{fontSize:11,color:T.muted}}>Receitas R$ {fmtMoney(item.receitas)} · Despesas R$ {fmtMoney(item.despesas)}</div>
              </div>
              <div style={{fontWeight:800,color:item.saldo>=0?T.green:T.red}}>R$ {fmtMoney(Math.abs(item.saldo))}{item.saldo<0?' -':' +'}</div>
            </div>) : <div style={{fontSize:12,color:T.muted}}>Nenhum lançamento na competência.</div>}
          </div>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14}}>
            <div style={{fontWeight:800,color:T.text,marginBottom:10}}>Contas mais movimentadas</div>
            {byAccount.length ? byAccount.map(item=><div key={`${item.accountCode}:${item.accountName}`} style={{display:"grid",gridTemplateColumns:"1fr auto",gap:8,padding:"9px 0",borderBottom:`1px solid ${T.border}`}}>
              <div>
                <div style={{fontWeight:700,color:T.text}}>{item.accountName}</div>
                <div style={{fontSize:11,color:T.muted}}>{item.accountCode || 'Sem código'} · Receitas R$ {fmtMoney(item.receitas)} · Despesas R$ {fmtMoney(item.despesas)}</div>
              </div>
              <div style={{fontWeight:800,color:item.saldo>=0?T.green:T.red}}>R$ {fmtMoney(Math.abs(item.saldo))}</div>
            </div>) : <div style={{fontSize:12,color:T.muted}}>Nenhuma conta movimentada na competência.</div>}
          </div>
        </div>

        <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr 1fr",gap:14}}>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14}}>
            <div style={{fontWeight:800,color:T.text,marginBottom:10}}>Rastreamento automático recente</div>
            {recentAutomatic.length ? recentAutomatic.map(item=><div key={item.id} style={{display:"grid",gridTemplateColumns:"1fr auto",gap:8,padding:"9px 0",borderBottom:`1px solid ${T.border}`}}>
              <div>
                <div style={{fontWeight:700,color:T.text}}>{item.description}</div>
                <div style={{fontSize:11,color:T.muted}}>{item.originModule || 'manual'} · {item.accountCode || item.accountName || item.category} · {fmtDate(item.date)}</div>
              </div>
              <div style={{fontWeight:800,color:item.type==='Receita'?T.green:T.red}}>R$ {fmtMoney(item.amount)}</div>
            </div>) : <div style={{fontSize:12,color:T.muted}}>Ainda não há lançamentos automáticos.</div>}
          </div>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14}}>
            <div style={{fontWeight:800,color:T.text,marginBottom:10}}>Tributos mapeados na competência</div>
            {monthTaxes.length ? monthTaxes.map(tax=><div key={tax.name} style={{display:"grid",gridTemplateColumns:"1fr auto",gap:8,padding:"9px 0",borderBottom:`1px solid ${T.border}`}}>
              <div>
                <div style={{fontWeight:700,color:T.text}}>{tax.name}</div>
                <div style={{fontSize:11,color:T.muted}}>{tax.count} lançamento(s) usando esse tributo/regime</div>
              </div>
              <div style={{fontWeight:800,color:T.text}}>Base R$ {fmtMoney(tax.totalBase)} · Tributos R$ {fmtMoney(tax.totalTax || 0)}</div>
            </div>) : <div style={{fontSize:12,color:T.muted}}>Nenhum tributo aplicado nesta competência.</div>}
          </div>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14}}>
            <div style={{fontWeight:800,color:T.text,marginBottom:10}}>Pendências tributárias</div>
            {pendingTaxSetup.length ? pendingTaxSetup.map(entry=><div key={entry.id} style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr auto",gap:8,padding:"9px 0",borderBottom:`1px solid ${T.border}`}}>
              <div>
                <div style={{fontWeight:700,color:T.text}}>{entry.description}</div>
                <div style={{fontSize:11,color:T.muted}}>{entry.accountCode || 'Sem conta'} · {entry.taxModel || 'Sem regime'} · {fmtDate(entry.date)}</div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontWeight:700,color:T.orange || T.accent}}>Configurar</span>
                {(canEdit || canDelete) && <Btn size="sm" variant="ghost" onClick={()=>openFinancialForm(entry)}>Abrir</Btn>}
              </div>
            </div>) : <div style={{fontSize:12,color:T.muted}}>Nenhum lançamento tributável sem detalhamento.</div>}
          </div>
        </div>
      </>}

      {tab === "entries" && <>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:10}}>
          <div style={{display:"flex",gap:8,flexWrap:'wrap'}}>
            <Select value={ft} onChange={e=>setFt(e.target.value)} style={{width:"auto"}}>
              <option value="Todos">Todos</option>
              {FIN_TYPES.map(t=><option key={t}>{t}</option>)}
            </Select>
            <Select value={fcc} onChange={e=>setFcc(e.target.value)} style={{width:"auto"}}>
              <option value="Todos">Todos os centros</option>
              {(SL.COST_CENTERS||COST_CENTERS).map(c=><option key={c}>{c}</option>)}
            </Select>
          </div>
          <div style={{fontSize:12,color:T.muted}}>Competência filtrada: <strong>{monthLabel(competenceMonth)}</strong></div>
        </div>
        <div style={{display:"grid",gap:12}}>
          {competenceValidated.map(entry=>{
            const isEditingEntry = !!form && !form._new && String(form.id||"")===String(entry.id);
            return <React.Fragment key={entry.id}>
              <div ref={el=>{ if(el) financialInlineRefs.current.set(String(entry.id), el); else financialInlineRefs.current.delete(String(entry.id)); }} onClick={(canEdit||canDelete)?(()=>openFinancialForm(entry)):undefined} style={{background:T.card,border:`1px solid ${isEditingEntry?T.accent:T.border}`,borderRadius:16,padding:14,cursor:(canEdit||canDelete)?"pointer":"default",boxShadow:`0 8px 20px ${T.shadow || "rgba(0,0,0,.06)"}`}}>
                <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"flex-start",flexWrap:"wrap"}}>
                  <div style={{minWidth:0}}>
                    <div style={{fontSize:11,fontWeight:800,color:T.accent,letterSpacing:.6,marginBottom:2}}>{displayFinancialRecordNumber(entry)}</div>
                    <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                      <Badge color={entry.type==="Receita"?T.green:T.red}>{entry.type}</Badge>
                      <Badge color={PAYMENT_STATUSES.has(String(entry.status||''))?T.green:(entry.status==='Cancelado'?T.red:T.accent)}>{entry.status}</Badge>
                      <CCBadge cc={entry.costCenter}/>
                    </div>
                    <div style={{fontWeight:800,color:T.text,fontSize:16,marginTop:8}}>{entry.description}</div>
                    <div style={{fontSize:12,color:T.muted,marginTop:4}}>{fmtDate(entry.date)} · {entry.accountName || entry.category}{entry.accountCode ? ` · ${entry.accountCode}` : ''}</div>
                    <div style={{fontSize:11,color:T.muted,marginTop:4}}>{entry.originModule || 'manual'}{entry.automatic ? ' · automático' : ''}{entry.documentNumber ? ` · doc ${entry.documentNumber}` : ''}</div>
                  </div>
                  <div style={{textAlign:isCompactViewport()?"left":"right"}}>
                    <div style={{fontWeight:800,color:entry.type==="Receita"?T.green:T.red,fontSize:16}}>R$ {fmtMoney(entry.amount)}</div>
                    {entry.dueDate && <div style={{fontSize:11,color:T.muted,marginTop:4}}>Venc. {fmtDate(entry.dueDate)}</div>}
                  </div>
                </div>
                {(entry._validation?.issues?.length || entry._validation?.warnings?.length) ? <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:10}}>{entry._validation?.issues?.length ? <Badge color={T.red}>{entry._validation.issues.length} bloqueio(s)</Badge> : null}{entry._validation?.warnings?.length ? <Badge color={T.orange || T.accent}>{entry._validation.warnings.length} aviso(s)</Badge> : null}</div> : null}
              </div>
              {isEditingEntry && form && !form._new && <div style={{marginTop:-2}} ref={el=>{ if(el) financialInlineRefs.current.set(String(entry.id), el); }}>{/* editor mantém ancorado abaixo do lançamento */}</div>}
            </React.Fragment>;
          })}
          {competenceValidated.length===0&&<div style={{padding:32,textAlign:"center",color:T.muted}}>Nenhum lançamento</div>}
        </div>
              </>}

      {tab === "payroll" && <>
        <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:14,marginBottom:16}}>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,marginBottom:8,flexWrap:"wrap"}}>
              <div>
                <div style={{fontWeight:800,color:T.text}}>Folha estimada</div>
                <div style={{fontSize:12,color:T.muted}}>Base salarial e encargos calculados para a competência selecionada.</div>
              </div>
              {(canCreate || canEdit) && <Btn onClick={generatePayrollMonth}>Gerar folha do mês</Btn>}
            </div>
            <div style={{fontSize:12,color:T.text}}>Colaboradores ativos: <strong>{payrollSummary.activeCount}</strong></div>
            <div style={{fontSize:12,color:T.text,marginTop:8}}>Base salarial: <strong>R$ {fmtMoney(payrollSummary.base)}</strong></div>
            <div style={{fontSize:12,color:T.text}}>Encargos: <strong>R$ {fmtMoney(payrollSummary.totalCharges)}</strong></div>
            <div style={{fontSize:13,color:T.green,fontWeight:800,marginTop:6}}>Custo real estimado: R$ {fmtMoney(payrollSummary.total)}</div>
          </div>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14}}>
            <div style={{fontWeight:800,color:T.text,marginBottom:10}}>Encargos editáveis</div>
            {payrollSummary.charges.length ? payrollSummary.charges.map(item=><div key={item.name} style={{display:"grid",gridTemplateColumns:"1fr auto",gap:8,padding:"9px 0",borderBottom:`1px solid ${T.border}`}}>
              <div>
                <div style={{fontWeight:700,color:T.text}}>{item.name}</div>
                <div style={{fontSize:11,color:T.muted}}>{Number(item?.pct || 0).toFixed(2)}% sobre a base</div>
              </div>
              <div style={{fontWeight:800,color:T.text}}>R$ {fmtMoney(item.value)}</div>
            </div>) : <div style={{fontSize:12,color:T.muted}}>Cadastre encargos editáveis no Admin.</div>}
          </div>
        </div>
        <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14,marginBottom:16}}>
          <div style={{fontWeight:800,color:T.text,marginBottom:10}}>Salários por colaborador</div>
          {payrollSummary.active.length ? payrollSummary.active.map(emp=><div key={emp.id || emp.name} style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr auto",gap:8,padding:"9px 0",borderBottom:`1px solid ${T.border}`}}>
            <div>
              <div style={{fontWeight:700,color:T.text}}>{emp.name || 'Colaborador'}</div>
              <div style={{fontSize:11,color:T.muted}}>{emp.role || 'Sem cargo'} · {emp.costCenter || emp.primaryFarmName || 'Sem centro de custo'}</div>
            </div>
            <div style={{fontWeight:800,color:T.text}}>R$ {fmtMoney(emp.salary || 0)}</div>
          </div>) : <div style={{fontSize:12,color:T.muted}}>Nenhum colaborador ativo encontrado.</div>}
        </div>
        <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14}}>
          <div style={{fontWeight:800,color:T.text,marginBottom:10}}>Lançamentos de folha na competência</div>
          {payrollEntriesForMonth.length ? payrollEntriesForMonth.map(entry=><div key={entry.id} style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr auto",gap:8,padding:"9px 0",borderBottom:`1px solid ${T.border}`}}>
            <div>
              <div style={{fontWeight:700,color:T.text}}>{entry.description}</div>
              <div style={{fontSize:11,color:T.muted}}>{entry.accountCode || entry.accountName} · {entry.status} · venc. {fmtDate(entry.dueDate)}</div>
            </div>
            <div style={{fontWeight:800,color:T.red}}>R$ {fmtMoney(entry.amount)}</div>
          </div>) : <div style={{fontSize:12,color:T.muted}}>Ainda não há lançamentos de folha nessa competência.</div>}
        </div>
      </>}

      {tab === "stock" && <>
        <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:14,marginBottom:16}}>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14}}>
            <div style={{fontWeight:800,color:T.text,marginBottom:10}}>Rastreio de custo por centro de custo</div>
            {stockByCostCenter.length ? stockByCostCenter.map(item=><div key={item.costCenter} style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr auto",gap:8,padding:"9px 0",borderBottom:`1px solid ${T.border}`}}>
              <div>
                <div style={{fontWeight:700,color:T.text}}>{item.costCenter}</div>
                <div style={{fontSize:11,color:T.muted}}>Consumo R$ {fmtMoney(item.consumption || 0)} · Descarte R$ {fmtMoney(item.discard || 0)} · Transferência saída R$ {fmtMoney(item.transferOut || 0)} · entrada R$ {fmtMoney(item.transferIn || 0)}</div>
              </div>
              <div style={{fontWeight:800,color:T.text}}>Movs. {item.moves || 0}</div>
            </div>) : <div style={{fontSize:12,color:T.muted}}>Sem rastreio de estoque nessa competência.</div>}
          </div>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14}}>
            <div style={{fontWeight:800,color:T.text,marginBottom:10}}>Origem e destino das transferências</div>
            {stockTransfers.length ? stockTransfers.map(entry=><div key={entry.id} style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr auto",gap:8,padding:"9px 0",borderBottom:`1px solid ${T.border}`}}>
              <div>
                <div style={{fontWeight:700,color:T.text}}>{entry.itemName}</div>
                <div style={{fontSize:11,color:T.muted}}>{fmtDate(entry.date)} · {entry.sourceCostCenter || entry.costCenter || 'Sem origem'} → {entry.destCostCenter || entry.costCenter || 'Sem destino'}</div>
              </div>
              <div style={{fontWeight:800,color:T.accent}}>R$ {fmtMoney(entry.total || 0)}</div>
            </div>) : <div style={{fontSize:12,color:T.muted}}>Nenhuma transferência interna no período.</div>}
          </div>
        </div>
        <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14}}>
          <div style={{fontWeight:800,color:T.text,marginBottom:10}}>Livro de consumo e custo do estoque</div>
          {stockLedger.length ? <div style={{display:"grid",gap:10}}>
            {stockLedger.map(entry=><div key={entry.id} style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"120px 1.3fr 110px 150px 150px 110px",gap:8,padding:"10px 0",borderBottom:`1px solid ${T.border}`}}>
              <div style={{fontSize:12,color:T.muted}}>{fmtDate(entry.date)}</div>
              <div>
                <div style={{fontWeight:700,color:T.text}}>{entry.itemName}</div>
                <div style={{fontSize:11,color:T.muted}}>{entry.kind} · {entry.qty.toLocaleString('pt-BR')} {entry.itemUnit}</div>
              </div>
              <div><Badge color={entry.kind==='Consumo'?T.red:(entry.kind==='Transferência'?T.accent:(entry.kind==='Entrada'?T.green:T.orange||T.accent))}>{entry.kind}</Badge></div>
              <div style={{fontSize:12,color:T.muted}}>{entry.sourceCostCenter || entry.costCenter || '-'}</div>
              <div style={{fontSize:12,color:T.muted}}>{entry.destCostCenter || (entry.kind==='Transferência' ? '-' : entry.costCenter || '-')}</div>
              <div style={{fontWeight:800,color:T.text}}>R$ {fmtMoney(entry.total || 0)}</div>
            </div>)}
          </div> : <div style={{fontSize:12,color:T.muted}}>Sem movimentos de estoque na competência.</div>}
        </div>
      </>}

            {tab === "closing" && <>
        <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1.1fr .9fr",gap:14,marginBottom:16}}>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,marginBottom:10,flexWrap:"wrap"}}>
              <div>
                <div style={{fontWeight:800,color:T.text}}>Trava opcional por competência</div>
                <div style={{fontSize:12,color:T.muted}}>Aberta permite editar; assistido alerta pendências; rígido bloqueia novos lançamentos e alterações.</div>
              </div>
              <Badge color={currentClosingLock.mode === "hard" ? T.red : (currentClosingLock.mode === "soft" ? (T.orange || T.accent) : T.green)}>{currentClosingLock.mode === "hard" ? "Travamento rígido" : (currentClosingLock.mode === "soft" ? "Fechamento assistido" : "Competência aberta")}</Badge>
            </div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:10}}>
              {(canEdit || canCreate) && <>
                <Btn variant={currentClosingLock.mode === "open" ? "primary" : "ghost"} onClick={()=>setClosingMode("open")}>Aberta</Btn>
                <Btn variant={currentClosingLock.mode === "soft" ? "primary" : "ghost"} onClick={()=>setClosingMode("soft")}>Assistido</Btn>
                <Btn variant={currentClosingLock.mode === "hard" ? "danger" : "ghost"} onClick={()=>{ if(!closingSummary.canStrictClose){ show("Ainda existem bloqueios críticos nesta competência.", "error"); return; } setClosingMode("hard"); }}>Travamento rígido</Btn>
              </>}
            </div>
            <div style={{fontSize:12,color:T.text,marginBottom:8}}>Responsável atual: <strong>{currentClosingLock.lockedBy || "Não definido"}</strong></div>
            <Textarea rows={2} value={currentClosingLock.notes || ""} onChange={e=>setClosingNotes(e.target.value)} placeholder="Observações do fechamento mensal"/>
          </div>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14}}>
            <div style={{fontWeight:800,color:T.text,marginBottom:10}}>Indicadores de eficiência</div>
            {efficiencyByCostCenter.slice(0,5).length ? efficiencyByCostCenter.slice(0,5).map(item=><div key={item.costCenter} style={{display:"grid",gridTemplateColumns:"1fr auto",gap:8,padding:"9px 0",borderBottom:`1px solid ${T.border}`}}>
              <div>
                <div style={{fontWeight:700,color:T.text}}>{item.costCenter}</div>
                <div style={{fontSize:11,color:T.muted}}>{item.rating} · Cobertura {item.coveragePct.toFixed(1)}%</div>
              </div>
              <div style={{fontWeight:800,color:item.color}}>{item.efficiencyPct.toFixed(1)}%</div>
            </div>) : <div style={{fontSize:12,color:T.muted}}>Sem centros suficientes para indicador nesta competência.</div>}
          </div>
        </div>

        <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:14,marginBottom:18}}>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14}}>
            <div style={{fontWeight:800,color:T.text,marginBottom:10}}>Checklist do fechamento da competência</div>
            <div style={{display:"grid",gap:10}}>
              {[
                {ok: closingSummary.entries > 0, label:`Competência com lançamentos (${closingSummary.entries})`},
                {ok: closingSummary.hardIssues === 0, label:`Sem bloqueios críticos (${closingSummary.hardIssues})`},
                {ok: closingSummary.withDocument === closingSummary.entries || competenceValidated.every(e=>String(e.justificationMissing||"").trim()), label:`Documento ou justificativa nos lançamentos`},
                {ok: closingSummary.withTaxes === competenceValidated.filter(e=>e._validation.account?.taxable).length || competenceValidated.filter(e=>e._validation.account?.taxable).every(e=>String(e.justificationMissing||"").trim()), label:`Tributos definidos nas contas tributáveis`},
                {ok: competenceValidated.filter(e=>PAYMENT_STATUSES.has(String(e.status||""))).every(e=>String(e.paymentDate||"").trim()), label:`Pagos/recebidos com data de liquidação`},
              ].map((item,idx)=><div key={idx} style={{display:"grid",gridTemplateColumns:"auto 1fr",gap:8,alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${T.border}`}}>
                <span style={{fontSize:18}}>{item.ok ? "✅" : "⛔"}</span>
                <div style={{fontSize:13,color:T.text,fontWeight:600}}>{item.label}</div>
              </div>)}
            </div>
          </div>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14}}>
            <div style={{fontWeight:800,color:T.text,marginBottom:10}}>Principais bloqueios</div>
            {issueBuckets.length ? issueBuckets.map(item=><div key={item.label} style={{display:"grid",gridTemplateColumns:"1fr auto",gap:8,padding:"9px 0",borderBottom:`1px solid ${T.border}`}}>
              <div style={{fontWeight:700,color:T.text}}>{item.label}</div>
              <div style={{fontWeight:800,color:T.red}}>{item.count}</div>
            </div>) : <div style={{fontSize:12,color:T.muted}}>Nenhum bloqueio crítico nesta competência.</div>}
          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:14}}>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14}}>
            <div style={{fontWeight:800,color:T.text,marginBottom:10}}>Lançamentos com bloqueio</div>
            {hardIssues.length ? hardIssues.map(entry=><div key={entry.id} style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr auto",gap:8,padding:"9px 0",borderBottom:`1px solid ${T.border}`}}>
              <div>
                <div style={{fontWeight:700,color:T.text}}>{entry.description}</div>
                <div style={{fontSize:11,color:T.muted}}>{entry.accountCode || 'Sem conta'} · {entry.status} · {fmtDate(entry.date)}</div>
                <div style={{fontSize:11,color:T.red,marginTop:4}}>{entry._validation.issues.join(' · ')}</div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontWeight:800,color:entry.type==='Receita'?T.green:T.red}}>R$ {fmtMoney(entry.amount)}</span>
                {(canEdit || canDelete) && <Btn size="sm" variant="ghost" onClick={()=>openFinancialForm(entry)}>Abrir</Btn>}
              </div>
            </div>) : <div style={{fontSize:12,color:T.muted}}>Nenhum lançamento bloqueando o fechamento.</div>}
          </div>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14}}>
            <div style={{fontWeight:800,color:T.text,marginBottom:10}}>Lançamentos com aviso</div>
            {warningEntries.length ? warningEntries.map(entry=><div key={entry.id} style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr auto",gap:8,padding:"9px 0",borderBottom:`1px solid ${T.border}`}}>
              <div>
                <div style={{fontWeight:700,color:T.text}}>{entry.description}</div>
                <div style={{fontSize:11,color:T.muted}}>{entry.accountCode || 'Sem conta'} · {entry.status} · {fmtDate(entry.date)}</div>
                <div style={{fontSize:11,color:T.orange || T.accent,marginTop:4}}>{entry._validation.warnings.join(' · ')}</div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontWeight:800,color:entry.type==='Receita'?T.green:T.red}}>R$ {fmtMoney(entry.amount)}</span>
                {(canEdit || canDelete) && <Btn size="sm" variant="ghost" onClick={()=>openFinancialForm(entry)}>Abrir</Btn>}
              </div>
            </div>) : <div style={{fontSize:12,color:T.muted}}>Nenhum aviso pendente nesta competência.</div>}
          </div>
        </div>
      </>}

      {tab === "reconciliation" && <>
        <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:14,marginBottom:18}}>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14}}>
            <div style={{fontWeight:800,color:T.text,marginBottom:10}}>Linha de conciliação da competência</div>
            {reconciliationEntries.length ? reconciliationEntries.map(entry=><div key={entry.id} style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr auto",gap:8,padding:"9px 0",borderBottom:`1px solid ${T.border}`}}>
              <div>
                <div style={{fontWeight:700,color:T.text}}>{entry.description}</div>
                <div style={{fontSize:11,color:T.muted}}>{entry.accountCode || entry.accountName || entry.category} · {entry.status} · {fmtDate(entry.dueDate || entry.date)}</div>
                <div style={{fontSize:11,color:entry._reconciliation.conciliated?T.green:(entry._reconciliation.overdue?T.red:(T.orange || T.accent)),marginTop:4}}>{entry._reconciliation.statusLabel}{entry.paymentDate ? ` · liquidação ${fmtDate(entry.paymentDate)}` : ''}</div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <Badge color={entry._reconciliation.conciliated?T.green:(entry._reconciliation.overdue?T.red:(T.orange || T.accent))}>{entry._reconciliation.statusLabel}</Badge>
                <span style={{fontWeight:800,color:entry.type==='Receita'?T.green:T.red}}>R$ {fmtMoney(entry.amount)}</span>
              </div>
            </div>) : <div style={{fontSize:12,color:T.muted}}>Sem lançamentos para conciliar na competência.</div>}
          </div>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14}}>
            <div style={{fontWeight:800,color:T.text,marginBottom:10}}>Fluxo diário da competência</div>
            {cashFlowTimeline.length ? cashFlowTimeline.map(item=><div key={item.date} style={{display:"grid",gridTemplateColumns:"1fr auto",gap:8,padding:"9px 0",borderBottom:`1px solid ${T.border}`}}>
              <div>
                <div style={{fontWeight:700,color:T.text}}>{fmtDate(item.date)}</div>
                <div style={{fontSize:11,color:T.muted}}>{item.qty} lançamento(s) · Receitas R$ {fmtMoney(item.receitas)} · Despesas R$ {fmtMoney(item.despesas)}</div>
              </div>
              <div style={{fontWeight:800,color:item.saldo>=0?T.green:T.red}}>R$ {fmtMoney(Math.abs(item.saldo))}{item.saldo < 0 ? ' -' : ' +'}</div>
            </div>) : <div style={{fontSize:12,color:T.muted}}>Sem movimentação diária consolidada na competência.</div>}
          </div>
        </div>
      </>}

      {tab === "reports" && <>
        <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:14,marginBottom:18}}>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14}}>
            <div style={{fontWeight:800,color:T.text,marginBottom:10}}>Resultado por origem automática</div>
            {reportByOrigin.length ? reportByOrigin.map(item=><div key={item.originModule} style={{display:"grid",gridTemplateColumns:"1fr auto",gap:8,padding:"9px 0",borderBottom:`1px solid ${T.border}`}}>
              <div>
                <div style={{fontWeight:700,color:T.text}}>{item.originModule || 'manual'}</div>
                <div style={{fontSize:11,color:T.muted}}>{item.count} lançamento(s) · {item.automatic} automático(s) · Receitas R$ {fmtMoney(item.receitas)} · Despesas R$ {fmtMoney(item.despesas)}</div>
              </div>
              <div style={{fontWeight:800,color:item.saldo>=0?T.green:T.red}}>R$ {fmtMoney(Math.abs(item.saldo))}</div>
            </div>) : <div style={{fontSize:12,color:T.muted}}>Sem origens movimentadas na competência.</div>}
          </div>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14}}>
            <div style={{fontWeight:800,color:T.text,marginBottom:10}}>Resultado por centro de custo</div>
            {[...byCostCenter, ...deniedCostCenters.filter(cc=>!byCostCenter.some(item=>item.costCenter===cc)).map(cc=>({costCenter:cc,_nd:true}))].length ? [...byCostCenter, ...deniedCostCenters.filter(cc=>!byCostCenter.some(item=>item.costCenter===cc)).map(cc=>({costCenter:cc,_nd:true}))].map(item=><div key={item.costCenter} style={{display:"grid",gridTemplateColumns:"1fr auto",gap:8,padding:"9px 0",borderBottom:`1px solid ${T.border}`}}>
              <div>
                <div style={{fontWeight:700,color:T.text}}>{item.costCenter}</div>
                <div style={{fontSize:11,color:T.muted}}>{item._nd ? 'Sem acesso a este centro de custo' : `Receitas R$ ${fmtMoney(item.receitas)} · Despesas R$ ${fmtMoney(item.despesas)}`}</div>
              </div>
              <div style={{fontWeight:800,color:item._nd?T.muted:(item.saldo>=0?T.green:T.red)}}>{item._nd ? 'ND' : `R$ ${fmtMoney(Math.abs(item.saldo))}${item.saldo < 0 ? ' -' : ' +'}`}</div>
            </div>) : <div style={{fontSize:12,color:T.muted}}>Sem dados por centro de custo na competência.</div>}
          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:14}}>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14}}>
            <div style={{fontWeight:800,color:T.text,marginBottom:10}}>Plano de contas movimentado</div>
            {byAccount.length ? byAccount.map(item=><div key={`${item.accountCode}:${item.accountName}`} style={{display:"grid",gridTemplateColumns:"1fr auto",gap:8,padding:"9px 0",borderBottom:`1px solid ${T.border}`}}>
              <div>
                <div style={{fontWeight:700,color:T.text}}>{item.accountName}</div>
                <div style={{fontSize:11,color:T.muted}}>{item.accountCode || 'Sem código'} · Receitas R$ {fmtMoney(item.receitas)} · Despesas R$ {fmtMoney(item.despesas)}</div>
              </div>
              <div style={{fontWeight:800,color:item.saldo>=0?T.green:T.red}}>R$ {fmtMoney(Math.abs(item.saldo))}</div>
            </div>) : <div style={{fontSize:12,color:T.muted}}>Sem contas movimentadas na competência.</div>}
          </div>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14}}>
            <div style={{fontWeight:800,color:T.text,marginBottom:10}}>Tributos por competência</div>
            {monthTaxes.length ? monthTaxes.map(tax=><div key={tax.name} style={{display:"grid",gridTemplateColumns:"1fr auto",gap:8,padding:"9px 0",borderBottom:`1px solid ${T.border}`}}>
              <div>
                <div style={{fontWeight:700,color:T.text}}>{tax.name}</div>
                <div style={{fontSize:11,color:T.muted}}>{tax.count} lançamento(s) · Base R$ {fmtMoney(tax.totalBase)}</div>
              </div>
              <div style={{fontWeight:800,color:T.text}}>R$ {fmtMoney(tax.totalTax || 0)}</div>
            </div>) : <div style={{fontSize:12,color:T.muted}}>Sem tributos apurados na competência.</div>}
          </div>
        </div>
      </>}

      {tab === "dre" && <>
        <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:14,marginBottom:16}}>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14}}>
            <div style={{fontWeight:800,color:T.text,marginBottom:10}}>DRE gerencial da competência</div>
            {[
              ["Receita bruta", dreSummary.receitaBruta, T.green],
              ["(-) Deduções / tributos", dreSummary.deducoes, T.orange || T.accent],
              ["= Receita líquida", dreSummary.receitaLiquida, T.text],
              ["(-) Custo de estoque consumido", dreSummary.custoEstoqueConsumido, T.red],
              ["(-) Custo da folha", dreSummary.custoFolha, T.red],
              ["(-) Serviços terceirizados", dreSummary.custoServicosTerceiros, T.red],
              ["(-) Despesas operacionais", dreSummary.despesasOperacionais, T.red],
              ["= Resultado operacional", dreSummary.resultadoOperacional, dreSummary.resultadoOperacional>=0?T.green:T.red],
            ].map(([label, value, color])=><div key={label} style={{display:"grid",gridTemplateColumns:"1fr auto",gap:8,padding:"10px 0",borderBottom:`1px solid ${T.border}`}}>
              <div style={{fontWeight:700,color:T.text}}>{label}</div>
              <div style={{fontWeight:800,color}}>R$ {fmtMoney(Math.abs(value || 0))}</div>
            </div>)}
          </div>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14}}>
            <div style={{fontWeight:800,color:T.text,marginBottom:10}}>Eficiência por centro de custo</div>
            {[...efficiencyByCostCenter, ...deniedCostCenters.filter(cc=>!efficiencyByCostCenter.some(item=>item.costCenter===cc)).map(cc=>({costCenter:cc,_nd:true}))].length ? [...efficiencyByCostCenter, ...deniedCostCenters.filter(cc=>!efficiencyByCostCenter.some(item=>item.costCenter===cc)).map(cc=>({costCenter:cc,_nd:true}))].map(item=><div key={item.costCenter} style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr auto",gap:8,padding:"9px 0",borderBottom:`1px solid ${T.border}`}}>
              <div>
                <div style={{fontWeight:700,color:T.text}}>{item.costCenter}</div>
                <div style={{fontSize:11,color:T.muted}}>{item._nd ? 'Sem acesso a este centro de custo' : `Receita operacional R$ ${fmtMoney(item.operacionalReceita)} · Despesa operacional R$ ${fmtMoney(item.totalDespesaRef)}`}</div>
                <div style={{fontSize:11,color:item._nd?T.muted:item.color,fontWeight:700}}>{item._nd ? 'ND' : `${item.rating} · Cobertura ${item.coveragePct.toFixed(1)}%`}</div>
              </div>
              <div style={{textAlign:isCompactViewport()?"left":"right"}}>
                <div style={{fontWeight:800,color:item._nd?T.muted:(item.margem>=0?T.green:T.red)}}>{item._nd ? 'ND' : `R$ ${fmtMoney(Math.abs(item.margem))}`}</div>
                <div style={{fontSize:11,color:T.muted}}>Margem operacional</div>
              </div>
            </div>) : <div style={{fontSize:12,color:T.muted}}>Sem dados suficientes nesta competência.</div>}
          </div>
        </div>
      </>}

{tab === "pending" && <>
        <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr 1fr",gap:14}}>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14}}>
            <div style={{fontWeight:800,color:T.text,marginBottom:10}}>Pendências de documento ou justificativa</div>
            {pendingDocs.length ? pendingDocs.map(entry=><div key={entry.id} style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr auto",gap:8,padding:"9px 0",borderBottom:`1px solid ${T.border}`}}>
              <div>
                <div style={{fontWeight:700,color:T.text}}>{entry.description}</div>
                <div style={{fontSize:11,color:T.muted}}>{entry.originModule || 'manual'} · {entry.accountName || entry.category} · {fmtDate(entry.date)}</div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontWeight:700,color:entry.type==='Receita'?T.green:T.red}}>R$ {fmtMoney(entry.amount)}</span>
                {(canEdit || canDelete) && <Btn size="sm" variant="ghost" onClick={()=>openFinancialForm(entry)}>Abrir</Btn>}
              </div>
            </div>) : <div style={{fontSize:12,color:T.muted}}>Nenhum lançamento sem documento/justificativa.</div>}
          </div>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14}}>
            <div style={{fontWeight:800,color:T.text,marginBottom:10}}>Pendências vencidas</div>
            {pendingPayments.length ? pendingPayments.map(entry=><div key={entry.id} style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr auto",gap:8,padding:"9px 0",borderBottom:`1px solid ${T.border}`}}>
              <div>
                <div style={{fontWeight:700,color:T.text}}>{entry.description}</div>
                <div style={{fontSize:11,color:T.muted}}>{entry.status} · vencimento {fmtDate(entry.dueDate)} · {entry.costCenter}</div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontWeight:700,color:entry.type==='Receita'?T.green:T.red}}>R$ {fmtMoney(entry.amount)}</span>
                {(canEdit || canDelete) && <Btn size="sm" variant="ghost" onClick={()=>openFinancialForm(entry)}>Abrir</Btn>}
              </div>
            </div>) : <div style={{fontSize:12,color:T.muted}}>Nenhum lançamento vencido.</div>}
          </div>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14}}>
            <div style={{fontWeight:800,color:T.text,marginBottom:10}}>Pendências tributárias</div>
            {pendingTaxSetup.length ? pendingTaxSetup.map(entry=><div key={entry.id} style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr auto",gap:8,padding:"9px 0",borderBottom:`1px solid ${T.border}`}}>
              <div>
                <div style={{fontWeight:700,color:T.text}}>{entry.description}</div>
                <div style={{fontSize:11,color:T.muted}}>{entry.accountCode || 'Sem conta'} · {entry.taxModel || 'Sem regime'} · {fmtDate(entry.date)}</div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontWeight:700,color:T.orange || T.accent}}>Configurar</span>
                {(canEdit || canDelete) && <Btn size="sm" variant="ghost" onClick={()=>openFinancialForm(entry)}>Abrir</Btn>}
              </div>
            </div>) : <div style={{fontSize:12,color:T.muted}}>Nenhum lançamento tributável sem detalhamento.</div>}
          </div>
        </div>
      </>}
      {form && <div ref={financialInlineTopRef} style={{marginTop:18,background:T.card,border:`1px solid ${T.border}`,borderRadius:18,padding:isCompactViewport()?16:20,boxShadow:`0 12px 28px ${T.shadow || "rgba(0,0,0,.08)"}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:isCompactViewport()?"flex-start":"center",gap:12,flexWrap:"wrap",marginBottom:14}}>
          <div>
            <div style={{fontSize:11,fontWeight:800,color:T.accent,textTransform:"uppercase",letterSpacing:.8}}>Fluxo inline</div>
            {!form._new && <div style={{fontSize:12,fontWeight:800,color:T.accent,letterSpacing:.6,marginBottom:4}}>{displayFinancialRecordNumber(form)}</div>}
            <div style={{fontSize:isCompactViewport()?18:20,fontWeight:800,color:T.text,marginTop:4}}>{form._new?"Novo Lançamento":"Editar Lançamento"}</div>
            <div style={{fontSize:12,color:T.muted,marginTop:4}}>Edite e salve sem sair da tela do financeiro.</div>
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <Btn variant="ghost" onClick={()=>setForm(null)}>Fechar editor</Btn>
            {((form && form._new && canCreate) || (form && !form._new && canEdit)) && <Btn onClick={save}>Salvar agora</Btn>}
          </div>
        </div>
        {form && <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:14}}>
          <Field label="Tipo">
            <Select value={form.type} onChange={e=>setForm(f=>({...f,type:e.target.value}))}>
              {FIN_TYPES.map(t=><option key={t}>{t}</option>)}
            </Select>
          </Field>
          <Field label="Status">
            <Select value={form.status || 'Lançado'} onChange={e=>setForm(f=>({...f,status:e.target.value}))}>
              {STATUS_OPTIONS.map(s=><option key={s}>{s}</option>)}
            </Select>
          </Field>
          <Field label="Categoria">
            <Select value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))}>
              {(SL.FIN_CATS||FIN_CATS).map(c=><option key={c}>{c}</option>)}
            </Select>
          </Field>
          <Field label="Conta contábil">
            <Select value={form.accountCode||""} onChange={e=>{ const acc=(chartAccounts||[]).find(a=>String(a.code)===String(e.target.value)); setForm(f=>({...f,accountCode:e.target.value,accountName:acc?.name||"",costCenter:acc?.costCenter||f.costCenter})); }}>
              <option value="">Selecionar automaticamente</option>
              {(chartAccounts||[]).map(acc=><option key={`${acc.code}:${acc.name}`} value={acc.code}>{acc.code} · {acc.name}</option>)}
            </Select>
          </Field>
          <Field label="Documento / NF"><Input value={form.documentNumber||""} onChange={e=>setForm(f=>({...f,documentNumber:e.target.value}))}/></Field>
          <Field label="Descrição *" col="1/-1"><Input value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))}/></Field>
          <Field label="Valor (R$) *">
            <div style={{position:"relative"}}>
              <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:T.muted,fontSize:13,pointerEvents:"none"}}>R$</span>
              <Input type="number" min="0" step="0.01" value={form.amount} onChange={e=>setForm(f=>{ const next={...f,amount:Number(e.target.value)}; return {...next,taxBreakdown:buildTaxBreakdown(next)}; })} style={{paddingLeft:30}}/>
            </div>
          </Field>
          <Field label="Competência"><Input type="month" value={form.competenceMonth || monthFromDate(form.date)} onChange={e=>setForm(f=>({...f,competenceMonth:e.target.value}))}/></Field>
          <Field label="Data"><Input type="date" value={form.date} onChange={e=>setForm(f=>({...f,date:e.target.value,competenceMonth:f.competenceMonth || monthFromDate(e.target.value)}))}/></Field>
          <Field label="Vencimento"><Input type="date" value={form.dueDate||form.date} onChange={e=>setForm(f=>({...f,dueDate:e.target.value}))}/></Field>
          <Field label="Pagamento / recebimento"><Input type="date" value={form.paymentDate||""} onChange={e=>setForm(f=>({...f,paymentDate:e.target.value}))}/></Field>
          <Field label="Forma de pagamento"><Input value={form.paymentMethod||""} onChange={e=>setForm(f=>({...f,paymentMethod:e.target.value}))}/></Field>
          <Field label="Centro">
            <Select value={form.costCenter} onChange={e=>setForm(f=>({...f,costCenter:e.target.value}))}>
              {(SL.COST_CENTERS||COST_CENTERS).map(c=><option key={c}>{c}</option>)}
            </Select>
          </Field>
          <Field label="Centro contraparte"><Input value={form.counterCostCenter||""} onChange={e=>setForm(f=>({...f,counterCostCenter:e.target.value}))}/></Field>
          <Field label="Regime tributário / modelo">
            <Select value={form.taxModel||taxRegimes?.[0]?.name||'Atual'} onChange={e=>setForm(f=>({...f,taxModel:e.target.value,taxBreakdown:buildTaxBreakdown({...f,taxModel:e.target.value})}))}>
              {(taxRegimes || []).map(regime=><option key={regime?.name || JSON.stringify(regime)} value={regime?.name || ''}>{regime?.name || 'Sem nome'}</option>)}
              {!taxRegimes.length && <option>Atual</option>}
            </Select>
          </Field>
          <Field label="Tributos do lançamento" col="1/-1">
            <div style={{display:"grid",gap:8}}>
              {(Array.isArray(form.taxBreakdown) ? form.taxBreakdown : buildTaxBreakdown(form)).map((tax, idx)=>{
                const list = Array.isArray(form.taxBreakdown) ? form.taxBreakdown : buildTaxBreakdown(form);
                return <div key={`${tax.name}:${idx}`} style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr 1fr":"1.3fr 90px 120px auto",gap:8,alignItems:"end",padding:10,border:`1px solid ${T.border}`,borderRadius:12}}>
                  <div>
                    <div style={{fontSize:12,color:T.muted,marginBottom:4}}>Tributo</div>
                    <div style={{fontWeight:700,color:T.text}}>{tax.name}</div>
                  </div>
                  <div>
                    <div style={{fontSize:12,color:T.muted,marginBottom:4}}>%</div>
                    <Input type="number" min="0" step="0.01" value={tax.pct ?? 0} onChange={e=>setForm(f=>({
                      ...f,
                      taxBreakdown:(Array.isArray(f.taxBreakdown)?f.taxBreakdown:list).map((item,i)=>i===idx?{...item,pct:Number(e.target.value||0),amount:Number((((Number(f.amount||0))*Number(e.target.value||0))/100).toFixed(2))}:item)
                    }))}/>
                  </div>
                  <div>
                    <div style={{fontSize:12,color:T.muted,marginBottom:4}}>Valor R$</div>
                    <Input type="number" min="0" step="0.01" value={tax.amount ?? 0} onChange={e=>setForm(f=>({
                      ...f,
                      taxBreakdown:(Array.isArray(f.taxBreakdown)?f.taxBreakdown:list).map((item,i)=>i===idx?{...item,amount:Number(e.target.value||0),pct:Number(f.amount||0)>0?Number(((Number(e.target.value||0)/Number(f.amount||0))*100).toFixed(4)):0}:item)
                    }))}/>
                  </div>
                  <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:T.muted,paddingBottom:8}}><input type="checkbox" checked={tax.included !== false} onChange={e=>setForm(f=>({
                    ...f,
                    taxBreakdown:(Array.isArray(f.taxBreakdown)?f.taxBreakdown:list).map((item,i)=>i===idx?{...item,included:e.target.checked}:item)
                  }))}/>Aplicar</label>
                </div>
              })}
              {!getTaxesForModel(form.taxModel).length && <div style={{fontSize:12,color:T.muted}}>O regime atual não possui tributos pré-configurados. Você pode cadastrar tributos em Admin &gt; Listas &gt; Regimes e Tributos.</div>}
            </div>
          </Field>
          <Field label="Justificativa se faltar valor/NF ou tributo" col="1/-1"><Input value={form.justificationMissing||""} onChange={e=>setForm(f=>({...f,justificationMissing:e.target.value}))}/></Field>
          <Field label="Obs." col="1/-1"><Textarea rows={2} value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/></Field>
          {(()=>{ const validation = validateEntry({ ...form, accountCode: form.accountCode || (chartAccounts.find(acc=>String(acc.code)===String(form.accountCode||"")) || categoryToAccount(form.category, form.type))?.code || "", accountName: form.accountName || (chartAccounts.find(acc=>String(acc.code)===String(form.accountCode||"")) || categoryToAccount(form.category, form.type))?.name || "", taxBreakdown: Array.isArray(form.taxBreakdown) ? form.taxBreakdown : buildTaxBreakdown(form) }); return <div style={{gridColumn:"1/-1",display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:12,padding:12,border:`1px solid ${validation.issues.length ? T.red : (validation.warnings.length ? (T.orange || T.accent) : T.border)}`,borderRadius:12,background:validation.issues.length?`${T.red}10`:(validation.warnings.length?`${T.accent}10`:'transparent')}}>
            <div>
              <div style={{fontWeight:800,color:T.text,marginBottom:6}}>Validação do lançamento</div>
              <div style={{fontSize:12,color:validation.issues.length?T.red:(validation.warnings.length?(T.orange || T.accent):T.green),fontWeight:700}}>{validation.issues.length ? `${validation.issues.length} bloqueio(s)` : (validation.warnings.length ? `${validation.warnings.length} aviso(s)` : 'Pronto para salvar')}</div>
            </div>
            <div style={{display:"grid",gap:4}}>
              {validation.issues.map((msg,idx)=><div key={`i:${idx}`} style={{fontSize:12,color:T.red}}>⛔ {msg}</div>)}
              {!validation.issues.length && validation.warnings.map((msg,idx)=><div key={`w:${idx}`} style={{fontSize:12,color:T.orange || T.accent}}>⚠️ {msg}</div>)}
              {!validation.issues.length && !validation.warnings.length && <div style={{fontSize:12,color:T.green}}>✅ Todos os campos críticos desta regra estão preenchidos.</div>}
              {formCompetenceLock?.mode === "soft" && <div style={{fontSize:12,color:T.orange || T.accent}}>⚠️ Esta competência está em fechamento assistido. Revise o lançamento antes de salvar.</div>}
              {formCompetenceLock?.mode === "hard" && <div style={{fontSize:12,color:T.red}}>⛔ Esta competência está com travamento rígido e bloqueia alterações.</div>}
            </div>
          </div>; })()}
          <div style={{gridColumn:"1/-1",display:"flex",gap:10,justifyContent:"flex-end"}}>
            {canDelete && !form._new && <Btn variant="danger" onClick={()=>{ const lockState = getClosingLockForMonth(String(form?.competenceMonth || monthFromDate(form?.date || today())).slice(0,7)); if(lockState?.mode === "hard"){ show(`Competência ${monthLabel(lockState.monthKey)} está com travamento rígido.`, "error"); return; } persistEntries(p=>p.map(e=>String(e.id)===String(form.id)?markFinancialUpdatePending({...e,_deleted:true,deleted:true,deletedAt:new Date().toISOString(),deletedBy:session?.name||"",...makeUpdateMeta(session?.name||"")}, "delete"):e));setForm(null);syncOnSave("financial");}}>Excluir</Btn>}
            <Btn variant="ghost" onClick={()=>setForm(null)}>Cancelar</Btn>
            {((form && form._new && canCreate) || (form && !form._new && canEdit)) && <Btn onClick={save}>Salvar</Btn>}
          </div>
        </div>}
      </div>}
    </div>;
  };
}
